import React, { useContext, useEffect, useState } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import Logo2 from "../asset/images/rrlogo2.png"
import ProfileDropdown from "../Component/Common/ProfileDropdown";
import ContentHeader from "../Component/Common/ContentHeader";
import Sidebar from "../Component/Common/Sidebar";
import { Menu, PhoneCallIcon, Plus } from "lucide-react";
import MobileBottomNav from "../Component/Common/MobileBottomNav";

import { BASE_URL } from "../config/api";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { Bell } from "lucide-react";

// Professional mock data to guarantee your layout functions if the API is empty/failing



function NotificationBell({ count, notifications, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const dueDotColors = {
    overdue: "bg-red-500",
    today: "bg-amber-400",
    tomorrow: "bg-blue-400",
    upcoming: "bg-gray-300",
  };

  // Show up to 3 recent notifications in the dropdown
  const recent = notifications.slice(0, 3);

  return (
    <div className="relative" ref={ref}>
      {/* Injecting the ringing keyframes */}
      <style>{`
        @keyframes ring {
          0% { transform: rotate(0); }
          5% { transform: rotate(15deg); }
          10% { transform: rotate(-15deg); }
          15% { transform: rotate(12deg); }
          20% { transform: rotate(-12deg); }
          25% { transform: rotate(8deg); }
          30% { transform: rotate(-8deg); }
          35% { transform: rotate(4deg); }
          40% { transform: rotate(-4deg); }
          45% { transform: rotate(0); }
          100% { transform: rotate(0); }
        }
        .animate-bell-ring {
          animation: ring 2.5s ease-in-out infinite;
          transform-origin: top center;
        }
      `}</style>

      {/* Bell button */}
      <button
        onClick={() => setOpen((p) => !p)}
        className="relative flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors"
      >
        {/* The Bell icon wrapper gets the animation if count > 0 */}
        <div className={count > 0 ? "animate-bell-ring" : ""}>
          <Bell className="w-5 h-5 text-white fill-white/10" />
        </div>
        
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-semibold leading-none px-0.5 shadow-sm">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {/* Dropdown - FIXED RESPONSIVE BOUNDS FOR MOBILE */}
      {open && (
        <div className="fixed sm:absolute right-4 sm:right-0 left-4 sm:left-auto mt-2 w-[calc(100vw-32px)] sm:w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">Follow-up Reminders</span>
            {count > 0 && (
              <span className="bg-red-50 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-200">
                {count} new
              </span>
            )}
          </div>

          {/* Reminder list */}
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
            {recent.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-2xl mb-1">🔔</p>
                <p className="text-sm text-gray-400">No new reminders</p>
              </div>
            ) : (
              recent.map((n, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setOpen(false);
                    navigate("/ret-reminder");
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-start gap-3"
                >
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    {(n.customer_name || n.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {n.customer_name || n.name || "Customer"}
                    </p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {n.remark || n.note || n.message || "Follow-up due"}
                    </p>
                    <p className="text-xs text-blue-500 mt-0.5 font-medium">
                      {n.due_label || n.dueLabel || ""}
                    </p>
                  </div>
                  <span
                    className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      dueDotColors[n.dueType || n.due_type || "upcoming"]
                    }`}
                  />
                </button>
              ))
            )}
          </div>

          {/* View all footer */}
          <div className="border-t border-gray-100 px-4 py-2.5">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/ret-reminder");
              }}
              className="w-full text-center text-sm font-semibold text-blue-600 hover:text-blue-700 py-1 rounded-lg hover:bg-blue-50 transition-colors"
            >
              View all reminders →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ... (Keep the remainder of your HubPanel component exactly identical)
export default function HubPanel() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
  const [showZohoPopup, setShowZohoPopup] = useState(false);
  const [activating, setActivating] = useState(false);
  const [forceLogoutDetected, setForceLogoutDetected] = useState(false);
  const [checkingLogoutStatus, setCheckingLogoutStatus] = useState(false);

  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showMpinPopup, setShowMpinPopup] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isCheckoutPage = location.pathname === "/ordersummery";
  const { user, logout, login } = useContext(AuthContext);
  const authUser = user;

  const role = Number(authUser?.role);
  const isRetailer = role === 5 || role === 6;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const isRetailerMobile = isRetailer && isMobile;
  const isCustomerRole = [3, 4, 5, 6, 7].includes(role);
  
  const useMobileCustomerUI = isCustomerRole && isMobile;

  /* ---------------- FETCH REMINDERS VIA API WITH MOCK FALLBACK ---------------- */
 useEffect(() => {
  if (!authUser?.id) return;

  const fetchReminders = async () => {
    try {
      const user =
        JSON.parse(localStorage.getItem("authUserData")) || {};

      const params = {
        reminder_type: "sales_followup",
        reminder_user_id: user?.id,
        from_date: "2026-06-01",
        to_date: "2026-06-30",
        search: "",
        per_page: 20,
        page: 1,
      };

      if (user?.location_mode === "state") {
        params.states = user?.state;
      }

      if (user?.location_mode === "district") {
        params.districts = user?.district;
      }

      const { data } = await axios.get(
        `${BASE_URL}warranty/followup-retailers`,
        { params }
      );

      if (data?.success) {
        const formattedNotifications = (data.data || []).map((item) => {
          let due_type = "upcoming";
          let due_label = "Upcoming";

          const reminderDate =
            item.reminder_user_date ||
            item.reminder_system_date;

          if (reminderDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const due = new Date(reminderDate);
            due.setHours(0, 0, 0, 0);

            const diffDays = Math.round(
              (due.getTime() - today.getTime()) /
                (1000 * 60 * 60 * 24)
            );

            if (diffDays < 0) {
              due_type = "overdue";
              due_label = `Overdue · ${Math.abs(diffDays)} day${
                Math.abs(diffDays) > 1 ? "s" : ""
              }`;
            } else if (diffDays === 0) {
              due_type = "today";
              due_label = "Due today";
            } else if (diffDays === 1) {
              due_type = "tomorrow";
              due_label = "Tomorrow";
            } else {
              due_type = "upcoming";
              due_label = `In ${diffDays} days`;
            }
          }

          return {
            id: item.id,
            customer_name:
              item.contact_person ||
              item.business_name ||
              "Retailer",

            remark:
              item.reminder_user_message ||
              "Follow-up reminder",

            due_label,
            due_type,

            // optional extra fields
            phone: item.contact_phone,
            email: item.contact_email,
            retailer: item.business_name,
            raw: item,
          };
        });

        setNotifications(formattedNotifications);
        setNotificationCount(formattedNotifications.length);
      } else {
        setNotifications([]);
        setNotificationCount(0);
      }
    } catch (error) {
      console.error(
        "Error fetching followup reminders:",
        error
      );
      setNotifications([]);
      setNotificationCount(0);
    }
  };

  fetchReminders();
}, [authUser?.id]);

  /* ---------------- ZOHO ACTIVATION ---------------- */
  const needsZohoActivation =
    authUser &&
    [2,4,5].includes(role) &&
    Number(authUser.is_verified) === 7 &&
    !authUser.zoho_id;

  useEffect(() => {
    if (needsZohoActivation) setShowZohoPopup(true);
  }, [needsZohoActivation]);

  const activateZohoAccount = async () => {
    try {
      setActivating(true);
      const companyId = authUser.company_id || 1;
      const userId = authUser.id;

      const response = await axios.get(
        `${BASE_URL}auto-zoho-contact-create`,
        {
          params: {
            company_id: companyId,
            user_id: userId,
          },
        }
      );

      const data = response.data;
      if (!data?.status) throw new Error(data?.message);

      const updatedUser = { ...authUser, zoho_id: data.zoho_id };
      login({ ...updatedUser, leads: authUser.leads || [] });

      setShowZohoPopup(false);
      alert("🎉 Account activated successfully!");
      window.location.reload();
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Failed to activate account.");
    } finally {
      setActivating(false);
    }
  };

  /* ---------------- ONBOARDING RULE ---------------- */
  const requiresOnboard = [2, 4, 5, 6];
  const isNotVerified =
    requiresOnboard.includes(role) &&
    (authUser?.is_verified === null || authUser?.is_verified < 7);

  useEffect(() => {
    if (isNotVerified && location.pathname !== "/onboard") {
      navigate("/onboard", { replace: true });
    }
  }, [isNotVerified, navigate, location.pathname]);

  /* ---------------- RESPONSIVE ---------------- */
  useEffect(() => {
    const onResize = () => {
      setSidebarOpen(window.innerWidth >= 640);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ---------------- FORCE LOGOUT STATUS ---------------- */
  useEffect(() => {
    if (!authUser?.id || ![1, 2, 5, 6].includes(Number(authUser?.role))) return;

    let intervalId;
    const checkLogoutStatus = async () => {
      try {
        setCheckingLogoutStatus(true);
        const response = await axios.get(`${BASE_URL}common/logout-status/${authUser.id}`);
        const data = response.data;
        const isForceLogout = data?.is_logout === true || data?.is_logout === 1;

        if (data?.status === true && isForceLogout) {
          setForceLogoutDetected(true);
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error("Logout status check failed", err);
      } finally {
        setCheckingLogoutStatus(false);
      }
    };

    checkLogoutStatus();
    intervalId = setInterval(checkLogoutStatus, 15000);
    return () => clearInterval(intervalId);
  }, [authUser?.id, authUser?.role]);

  /* ---------------- MPIN LOGIC ---------------- */
  useEffect(() => {
    const justLoggedIn = localStorage.getItem("just_logged_in") === "true";

    if (
      justLoggedIn &&
      authUser &&
      Number(authUser?.is_verified) === 7 &&
      (authUser?.is_mpin_active === null || Number(authUser?.is_mpin_active) === 0)
    ) {
      setShowMpinPopup(true);
      localStorage.removeItem("just_logged_in");
    }
  }, [authUser]);

  const handleSetMpin = () => {
    setShowMpinPopup(false);
    navigate("/profile");

    setTimeout(() => {
      const section = document.getElementById("mpin-section");
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300);
  };

  /* ---------------- LOGOUT ---------------- */
  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleForceLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const roleDisplayName = {
    1: "Admin",
    2: "Master Channel Partner",
    3: "Employee",
    4: "Channel Partner",
    5: "Authorized Retail Partner",
    6: "Promoter",
    7: "Customer",
  };

  const DEFAULT_LOGO1 = "/images/Logo.png";
  const DEFAULT_LOGO2 = "/images/white_logo.png";

  return (
    <div
     className={`min-h-screen text-slate-900 relative overflow-hidden z-0 ${
        isRetailerMobile
          ? "bg-gradient-to-br from-slate-300 via-blue-200 to-slate-500"
          : "bg-white"
      }`}
    >
      {isRetailerMobile && (
        <>
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-400/30 blur-[140px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-indigo-400/30 blur-[140px] rounded-full pointer-events-none" />
        </>
      )}

      {isRetailerMobile && (
        <div className="fixed top-0 left-0 right-0 z-40 px-4 py-1 bg-white/5 backdrop-blur-3xl border-b border-white/10 shadow-[0_20px_50px_rgba(37,99,235,0.22)]">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <h5 className="text-sm font-semibold text-slate-500">{authUser?.company_code}</h5>
              {(() => {
                const displayName =
                  authUser?.business_name ||
                  authUser?.contact_person ||
                  `${authUser?.first_name || ""} ${authUser?.last_name || ""}`;
                const isLong = displayName.length > 28;

                return isLong ? (
                  <div className="overflow-hidden max-w-[220px]">
                    <style>{`
                      @keyframes mq { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
                    `}</style>
                    <div className="whitespace-nowrap inline-block" style={{ animation: "mq 8s linear infinite" }}>
                      <span className="text-[13px] font-semibold text-slate-700 pr-10">{displayName}</span>
                      <span className="text-[13px] font-semibold text-slate-700 pr-10">{displayName}</span>
                    </div>
                  </div>
                ) : (
                  <span className="text-[13px] font-semibold text-slate-700 truncate block">{displayName}</span>
                );
              })()}
            </div>

            {role !== 5 && (
              <ProfileDropdown
                avatar={DEFAULT_LOGO1}
                userCode={authUser?.company_code || authUser?.employee_id || authUser?.id}
                onLogout={handleLogout}
                menuItems={[{ label: "Account", onClick: (n) => n("/profile") }]}
                isRetailerMobile={isRetailerMobile}
              />
            )}

            {role === 5 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/20 backdrop-blur-xl border border-white/30 shadow-[0_8px_20px_rgba(37,99,235,0.15)]">
                <div className="w-8 h-8 rounded-xl bg-slate-500/10 flex items-center justify-center">
                  <PhoneCallIcon className="text-blue-500 w-4 h-4"/>
                </div>
                <div className="leading-tight">
                  <p className="text-[11px] font-medium text-slate-500">Need Help?</p>
                  <a href="tel:8828272570" className="text-[12px] font-semibold text-blue-600">8828272570</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= HEADER ================= */}
      {!isRetailerMobile && (
        <header className="sticky top-0 z-40 bg-[#104191ff]">
          <div className="px-2">
            <div className="flex h-11 items-center justify-between">
              {/* LEFT */}
              <div className="flex items-center gap-2">
                {!isNotVerified && !useMobileCustomerUI && (
                  <button
                    onClick={() => setSidebarOpen((p) => !p)}
                    className="rounded-xl p-2 hover:bg-white/10 shrink-0"
                  >
                    <Menu size={20} className="text-white" />
                  </button>
                )}
                <img src={DEFAULT_LOGO2} className="h-10 sm:h-8 w-auto shrink-0 object-contain" alt="Logo" />
                <span className="hidden sm:inline text-sm font-semibold text-white/80 ml-1">
                  {authUser?.contact_person || authUser?.business_name || `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
                </span>
              </div>

              {/* RIGHT */}
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-xs text-slate-100 px-2">
                  Logged in as:{" "}
                  <span className="font-semibold">
                    {role === 3
                      ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
                      : roleDisplayName[role] ?? role}
                  </span>
                </div>

                <NotificationBell
                  count={notificationCount}
                  notifications={notifications}
                  navigate={navigate}
                />

                <ProfileDropdown
                  avatar={user?.photo_url || DEFAULT_LOGO1}
                  userCode={authUser?.company_code || authUser?.employee_id || authUser?.id}
                  onLogout={handleLogout}
                  menuItems={[{ label: "Account", onClick: (n) => n("/profile") }]}
                />
              </div>
            </div>

            {/* Mobile Title View */}
            <div className="flex sm:hidden items-center pb-1.5">
              {(() => {
                const displayName = authUser?.business_name || authUser?.contact_person || `${authUser?.first_name || ""} ${authUser?.last_name || ""}`;
                const isLong = displayName.length > 28;
                return isLong ? (
                  <div className="w-full overflow-hidden">
                    <style>{` @keyframes mq { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } } `}</style>
                    <div className="whitespace-nowrap" style={{ animation: "mq 8s linear infinite", display: "inline-block" }}>
                      <span className="text-[13px] font-semibold text-white/70 pr-10">{displayName}</span>
                      <span className="text-[13px] font-semibold text-white/70 pr-10">{displayName}</span>
                    </div>
                  </div>
                ) : (
                  <span className="text-[13px] font-semibold text-white/70">{displayName}</span>
                );
              })()}
            </div>
          </div>
        </header>
      )}

      {/* ================= LAYOUT ================= */}
      <div className={isRetailerMobile ? "" : "px-3 sm:px-6 lg:px-2"}>
        <div className={`${isRetailerMobile ? "" : "grid grid-cols-12 gap-3"}`}>
          {!isNotVerified && !useMobileCustomerUI && isMobile && (
            <>
              {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSidebarOpen(false)} />}
              <aside className={`fixed top-0 left-0 h-full w-[260px] bg-white z-50 shadow-xl transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
              </aside>
            </>
          )}

          {!isNotVerified && !useMobileCustomerUI && !isMobile && sidebarOpen && (
            <aside className="col-span-12 sm:col-span-5 md:col-span-4 lg:col-span-2 sticky top-14 max-h-[calc(100vh-20px)] overflow-y-auto">
              <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            </aside>
          )}

          {!isNotVerified && useMobileCustomerUI && <MobileBottomNav />}

          <main className={`${!isNotVerified && sidebarOpen && !isMobile ? "col-span-12 sm:col-span-7 md:col-span-8 lg:col-span-10" : "col-span-12"} transition-all`}>
            <div className={`h-[calc(100vh-20px)] overflow-y-auto ${isRetailerMobile ? "" : "bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100"}`}>
              {!isNotVerified && !isRetailerMobile && (
                <ContentHeader
                  name={authUser?.business_name}
                  city={authUser?.city}
                  email={authUser?.contact_email || authUser?.official_email}
                  mobile={authUser?.contact_phone || authUser?.official_phone}
                  loginUserCreated={authUser?.created_at}
                />
              )}

              <div className={`p-2 ${isRetailerMobile ? "mt-5 pt-16 pb-20" : ""}`}>
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>

      {!isNotVerified && isRetailerMobile && (Number(authUser?.role) === 5 || Number(authUser?.role) === 6) && location.pathname !== "/customer-create" && (
        <button
          onClick={() => navigate("/customer-create")}
          className="fixed bottom-24 right-5 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center shadow-[0_15px_40px_rgba(31,38,135,0.4)] hover:scale-110 active:scale-95 transition-all duration-300"
        >
          <Plus className="w-8 h-9" />
        </button>
      )}

      {/* ================= FOOTER ================= */}
      <footer className="border-t py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} {authUser?.title}
      </footer>

      {/* ================= ZOHO POPUP ================= */}
      {showZohoPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
            <h2 className="text-lg font-bold">Activate Your Account</h2>
            <p className="mt-2 text-sm text-slate-600">Activate your account to start using Dashboard.</p>
            <div className="flex gap-2">
              <button onClick={activateZohoAccount} disabled={activating} className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold">
                {activating ? "Activating..." : "Activate Now"}
              </button>
              <button onClick={handleLogout} className="mt-4 w-full rounded-xl bg-red-600 py-2 text-white font-semibold">Logout</button>
            </div>
          </div>
        </div>
      )}

      {forceLogoutDetected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
            <h2 className="text-lg font-bold text-rose-700">Session Terminated</h2>
            <p className="mt-2 text-sm font-semibold text-slate-600">
              You have been <span className="font-bold text-rose-600">force logged out</span> by your company.<br />
              Please contact support for further assistance.
            </p>
            <button onClick={handleForceLogout} className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700">Go to Login</button>
          </div>
        </div>
      )}

      {showMpinPopup && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`w-full max-w-sm rounded-[32px] p-6 text-center shadow-2xl border ${isRetailerMobile ? "bg-white/20 backdrop-blur-3xl border-white/20 text-white" : "bg-white border-slate-200 text-slate-900"}`}>
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center text-4xl shadow-lg ${isRetailerMobile ? "bg-white/20 backdrop-blur-xl border border-white/20" : "bg-blue-100"}`}>🔐</div>
            <h2 className={`mt-5 text-2xl font-bold ${isRetailerMobile ? "text-white" : "text-slate-800"}`}>Set Your MPIN</h2>
            <p className={`mt-3 text-sm leading-6 ${isRetailerMobile ? "text-slate-200" : "text-slate-500"}`}>For better security and quick login access, would you like to set your MPIN now?</p>
            <div className="mt-7 flex gap-3">
              <button onClick={() => setShowMpinPopup(false)} className={`flex-1 rounded-2xl py-3 font-semibold transition-all ${isRetailerMobile ? "bg-white/10 border border-white/20 text-white hover:bg-white/20" : "border border-slate-300 text-slate-600 hover:bg-slate-100"}`}>Later</button>
              <button onClick={handleSetMpin} className={`flex-1 rounded-2xl py-3 font-semibold transition-all ${isRetailerMobile ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-[0_10px_30px_rgba(59,130,246,0.45)]" : "bg-blue-600 text-white hover:bg-blue-700"}`}>Yes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// import React, { useContext, useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import Logo2 from "../asset/images/rrlogo2.png"
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu, PhoneCallIcon, Plus } from "lucide-react";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";

// import { BASE_URL } from "../config/api";
// import { AuthContext } from "../context/AuthContext";
// import axios from "axios";
// import socket from "../socket";
// import { Bell } from "lucide-react";

// function NotificationBell({ count, notifications, navigate }) {
//   const [open, setOpen] = useState(false);
//   const ref = React.useRef(null);

//   // Close on outside click
//   useEffect(() => {
//     const handler = (e) => {
//       if (ref.current && !ref.current.contains(e.target)) setOpen(false);
//     };
//     document.addEventListener("mousedown", handler);
//     return () => document.removeEventListener("mousedown", handler);
//   }, []);

//   const dueDotColors = {
//     overdue: "bg-red-500",
//     today: "bg-amber-400",
//     tomorrow: "bg-blue-400",
//     upcoming: "bg-gray-300",
//   };

//   // Show up to 5 recent notifications in the dropdown
//   const recent = notifications.slice(0, 5);

//   return (
//     <div className="relative" ref={ref}>
//       {/* Bell button */}
//       <button
//         onClick={() => setOpen((p) => !p)}
//         className="relative flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors"
//       >
//         <Bell className="w-5 h-5 text-white" />
//         {count > 0 && (
//           <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-semibold leading-none px-0.5">
//             {count > 99 ? "99+" : count}
//           </span>
//         )}
//       </button>

//       {/* Dropdown */}
//       {open && (
//         <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
//           {/* Header */}
//           <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
//             <span className="text-sm font-semibold text-gray-900">Follow-up Reminders</span>
//             {count > 0 && (
//               <span className="bg-red-50 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full border border-red-200">
//                 {count} new
//               </span>
//             )}
//           </div>

//           {/* Reminder list */}
//           <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
//             {recent.length === 0 ? (
//               <div className="px-4 py-8 text-center">
//                 <p className="text-2xl mb-1">🔔</p>
//                 <p className="text-sm text-gray-400">No new reminders</p>
//               </div>
//             ) : (
//               recent.map((n, i) => (
//                 <button
//                   key={i}
//                   onClick={() => {
//                     setOpen(false);
//                     navigate("/customer-followup-reminders");
//                   }}
//                   className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-start gap-3"
//                 >
//                   {/* Avatar */}
//                   <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
//                     {(n.customer_name || n.name || "?").slice(0, 2).toUpperCase()}
//                   </div>
//                   <div className="flex-1 min-w-0">
//                     <p className="text-sm font-semibold text-gray-900 truncate">
//                       {n.customer_name || n.name || "Customer"}
//                     </p>
//                     <p className="text-xs text-gray-500 truncate mt-0.5">
//                       {n.remark || n.note || n.message || "Follow-up due"}
//                     </p>
//                     <p className="text-xs text-blue-500 mt-0.5 font-medium">
//                       {n.due_label || n.dueLabel || ""}
//                     </p>
//                   </div>
//                   <span
//                     className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
//                       dueDotColors[n.dueType || n.due_type || "upcoming"]
//                     }`}
//                   />
//                 </button>
//               ))
//             )}
//           </div>

//           {/* View all footer */}
//           <div className="border-t border-gray-100 px-4 py-2.5">
//             <button
//               onClick={() => {
//                 setOpen(false);
//                 navigate("/customer-followup-reminders");
//               }}
//               className="w-full text-center text-sm font-semibold text-blue-600 hover:text-blue-700 py-1 rounded-lg hover:bg-blue-50 transition-colors"
//             >
//               View all reminders →
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);
//   const [forceLogoutDetected, setForceLogoutDetected] = useState(false);
//   const [checkingLogoutStatus, setCheckingLogoutStatus] = useState(false);

//   const [notificationCount, setNotificationCount] = useState(0);
//   const [notifications, setNotifications] = useState([]);
//   const [showMpinPopup, setShowMpinPopup] = useState(false);


//   const navigate = useNavigate();
//   const location = useLocation();

//   const isCheckoutPage = location.pathname === "/ordersummery";

//   // const authUserString = localStorage.getItem("authUserData");
//   // const authUser = authUserString ? JSON.parse(authUserString) : null;

//   // const [user, setUser] = useState(authUser);

//   const { user, logout, login } = useContext(AuthContext);
//   const authUser = user;



//   // useEffect(() => {
//   //   const syncUser = () => {
//   //     const updated = localStorage.getItem("authUserData");
//   //     if (updated) setUser(JSON.parse(updated));
//   //   };

//   //   window.addEventListener("storage", syncUser);
//   //   return () => window.removeEventListener("storage", syncUser);
//   // }, []);


//   const role = Number(authUser?.role);
//   const isRetailer = role === 5 || role === 6;
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
//   const isRetailerMobile = isRetailer && isMobile;
//   const isCustomerRole = [3, 4, 5, 6, 7].includes(role);

  
//   const useMobileCustomerUI = isCustomerRole && isMobile;

//   /* ---------------- ZOHO ACTIVATION ---------------- */
//   const needsZohoActivation =
//     authUser &&
//     [2,4,5].includes(role) &&
//     Number(authUser.is_verified) === 7 &&
//     !authUser.zoho_id;


//   useEffect(() => {

//     socket.on("remark_notification", (data) => {

//       // only roles 1,2,3
//       if (![1,2,3].includes(Number(authUser?.role))) return;

//       setNotificationCount((prev) => prev + 1);

//       setNotifications((prev) => [data, ...prev]);

//     });

//     return () => {
//       socket.off("remark_notification");
//     };

//   }, [authUser]);

//   useEffect(() => {
//     if (needsZohoActivation) setShowZohoPopup(true);
//   }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId =
//         authUser.company_id || 1;

//       const userId = authUser.id;

//       const response = await axios.get(
//         `${BASE_URL}auto-zoho-contact-create`,
//         {
//           params: {
//             company_id: companyId,
//             user_id: userId,
//           },
//         }
//       );

//       const data = response.data;

//       if (!data?.status) {
//         throw new Error(
//           data?.message
//         );
//       }

//       const updatedUser = {
//         ...authUser,
//         zoho_id: data.zoho_id,
//       };

//       login({
//         ...updatedUser,

//         leads:
//           authUser.leads || [],
//       });

//       setShowZohoPopup(false);

//       alert(
//         "🎉 Account activated successfully!"
//       );

//       window.location.reload();

//     } catch (err) {

//       alert(
//         err?.response?.data?.message ||
//         err?.message ||
//         "Failed to activate account."
//       );

//     } finally {

//       setActivating(false);

//     }
//   };

//   /* ---------------- AUTH REDIRECT ---------------- */
//   // useEffect(() => {
//   //   if (!authUser && !["/login", "/customer-login"].includes(location.pathname)) {
//   //     navigate("/login", { replace: true });
//   //   }
//   // }, [authUser, navigate, location.pathname]);

//   /* ---------------- ONBOARDING RULE ---------------- */
//   const requiresOnboard = [2, 4, 5, 6];
//   const isNotVerified =
//     requiresOnboard.includes(role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const onResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//       setIsMobile(window.innerWidth < 640);
//     };
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   useEffect(() => {
//   // ✅ ONLY FOR RETAILER (ROLE = 5)
//   if (
//     !authUser?.id ||
//     ![1, 2, 5, 6].includes(Number(authUser?.role))
//   ) return;

//   let intervalId;

//   const checkLogoutStatus = async () => {
//   try {
//     setCheckingLogoutStatus(true);

//     const response = await axios.get(
//       `${BASE_URL}common/logout-status/${authUser.id}`
//     );

//     const data = response.data;

//     const isForceLogout =
//       data?.is_logout === true ||
//       data?.is_logout === 1;

//     if (
//       data?.status === true &&
//       isForceLogout
//     ) {

//       setForceLogoutDetected(true);

//       clearInterval(intervalId);

//     }

//   } catch (err) {

//     console.error(
//       "Logout status check failed",
//       err
//     );

//   } finally {

//     setCheckingLogoutStatus(false);

//   }
// };

//   // 🔁 initial check
//   checkLogoutStatus();

//   // 🔁 poll every 15 seconds
//   intervalId = setInterval(checkLogoutStatus, 15000);

//   return () => clearInterval(intervalId);
// }, [authUser?.id, authUser?.role]);

// useEffect(() => {
//   const justLoggedIn =
//     localStorage.getItem("just_logged_in") === "true";

//   if (
//     justLoggedIn &&
//     authUser &&
//     Number(authUser?.is_verified) === 7 &&
//     (
//       authUser?.is_mpin_active === null ||
//       Number(authUser?.is_mpin_active) === 0
//     )
//   ) {
//     setShowMpinPopup(true);

//     localStorage.removeItem("just_logged_in");
//   }
// }, [authUser]);


//   const handleSetMpin = () => {
//     setShowMpinPopup(false);
//     navigate("/profile");

//     setTimeout(() => {
//       const section = document.getElementById("mpin-section");

//       if (section) {
//         section.scrollIntoView({
//           behavior: "smooth",
//           block: "start",
//         });
//       }
//     }, 300);
//   };

//   /* ---------------- LOGOUT ---------------- */
//   const handleLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };

//   const handleForceLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };


//   const roleDisplayName = {
//     1: "Admin",
//     2: "Master Channel Partner",
//     3: "Employee",
//     4: "Channel Partner",
//     5: "Authorized Retail Partner",
//     6: "Promoter",
//     7: "Customer",
//   };

//   const DEFAULT_LOGO = "https://warrantymitra.com/img/logo.png"; 
//   const DEFAULT_LOGO1 = "/images/Logo.png";
//   const DEFAULT_LOGO2 = "/images/white_logo.png";


//   return (
//     <div
//      className={`min-h-screen text-slate-900 relative overflow-hidden z-0 ${
//         isRetailerMobile
//           // ? "bg-gradient-to-br from-[#dbeafe] via-[#f0f9ff] to-[#e0f2fe]"
//           ? "bg-gradient-to-br from-slate-300 via-blue-200 to-slate-500"
//           : "bg-white"
//       }`}
//     >

//       {isRetailerMobile && (
//         <>
//           <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-400/30 blur-[140px] rounded-full pointer-events-none" />
//           <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-indigo-400/30 blur-[140px] rounded-full pointer-events-none" />
//         </>
//       )}

//       {isRetailerMobile && (
//         <div className="fixed top-0 left-0 right-0 z-40 
//                         px-4 py-1 
//                         bg-white/5 backdrop-blur-3xl
//                         border-b border-white/10 shadow-[0_20px_50px_rgba(37,99,235,0.22)]">

//           <div className="flex items-center justify-between">
//             <div className="min-w-0 flex-1">

//             {/* <p className="text-sm text-slate-700">
//               Hello 👋
//             </p> */}

//             <h5 className="text-sm font-semibold text-slate-500">
//               {authUser?.company_code}
//             </h5>

//             {(() => {
//               const displayName =
//                 authUser?.business_name ||
//                 authUser?.contact_person ||
//                 `${authUser?.first_name || ""} ${authUser?.last_name || ""}`;

//               const isLong = displayName.length > 28;

//               return isLong ? (
//                 <div className="overflow-hidden max-w-[220px]">
//                   <style>{`
//                     @keyframes mq {
//                       0% { transform: translateX(0); }
//                       100% { transform: translateX(-50%); }
//                     }
//                   `}</style>

//                   <div
//                     className="whitespace-nowrap inline-block"
//                     style={{
//                       animation: "mq 8s linear infinite",
//                     }}
//                   >
//                     <span className="text-[13px] font-semibold text-slate-700 pr-10">
//                       {displayName}
//                     </span>

//                     <span className="text-[13px] font-semibold text-slate-700 pr-10">
//                       {displayName}
//                     </span>
//                   </div>
//                 </div>
//               ) : (
//                 <span className="text-[13px] font-semibold text-slate-700 truncate block">
//                   {displayName}
//                 </span>
//               );
//             })()}
//           </div>

//             {/* <div className="w-10 h-10 rounded-full 
//                             bg-white/20 backdrop-blur-xl 
//                             border border-white/30
//                             flex items-center justify-center">
//               🏪
//             </div> */}

//             {role !== 5  &&(
//               <ProfileDropdown
//               avatar={DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//               isRetailerMobile={isRetailerMobile}
//             />
//             )}

//             {role === 5 && (
//               <div
//                 className="
//                   flex items-center gap-2
//                   px-3 py-2
//                   rounded-2xl
//                   bg-white/20 backdrop-blur-xl
//                   border border-white/30
//                   shadow-[0_8px_20px_rgba(37,99,235,0.15)]
//                 "
//               >
//                 <div className="w-8 h-8 rounded-xl bg-slate-500/10 flex items-center justify-center">
//                   <PhoneCallIcon className="text-blue-500 w-4 h-4"/>
//                 </div>

//                 <div className="leading-tight">
//                   <p className="text-[11px] font-medium text-slate-500">
//                     Need Help?
//                   </p>

//                   <a
//                     href="tel:8828272570"
//                     className="text-[12px] font-semibold text-blue-600"
//                   >
//                     8828272570
//                   </a>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       )}
//       {/* ================= HEADER ================= */}
//       {!isRetailerMobile && (
//         <header className="sticky top-0 z-40 bg-[#104191ff]">
//           <div className="px-2">

//             {/* ── Row 1: Hamburger | Logo | Bell | Avatar ── */}
//             <div className="flex h-11 items-center justify-between">

//               {/* LEFT */}
//               <div className="flex items-center gap-2">
//                 {!isNotVerified && !useMobileCustomerUI && (
//                   <button
//                     onClick={() => setSidebarOpen((p) => !p)}
//                     className="rounded-xl p-2 hover:bg-white/10 shrink-0"
//                   >
//                     <Menu size={20} className="text-white" />
//                   </button>
//                 )}

//                 <img
//                   src={DEFAULT_LOGO2}
//                   className="h-10 sm:h-8 w-auto shrink-0 object-contain"
//                   alt="Logo"
//                 />

//                 {/* Name inline — desktop only */}
//                 <span className="hidden sm:inline text-sm font-semibold text-white/80 ml-1">
//                   {authUser?.contact_person ||
//                     authUser?.business_name ||
//                     `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
//                 </span>
//               </div>

//               {/* RIGHT */}
//               <div className="flex items-center gap-3">
//                 {/* Role label — desktop only */}
//                 <div className="hidden sm:block text-xs text-slate-100 px-2">
//                   Logged in as:{" "}
//                   <span className="font-semibold">
//                     {role === 3
//                       ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
//                       : roleDisplayName[role] ?? role}
//                   </span>
//                 </div>

//                 {/* Bell */}
//                 {/* <div className="relative cursor-pointer shrink-0">
//                   <Bell className="w-5 h-5 text-white" />
//                   {notificationCount > 0 && (
//                     <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] rounded-full flex items-center justify-center">
//                       {notificationCount}
//                     </div>
//                   )}
//                 </div> */}

//                 <NotificationBell
//                   count={notificationCount}
//                   notifications={notifications}
//                   navigate={navigate}
//                 />


//                 {/* Avatar */}
//                 <ProfileDropdown
//                   avatar={user?.photo_url || DEFAULT_LOGO1}
//                   userCode={authUser?.company_code || authUser?.employee_id || authUser?.id}
//                   onLogout={handleLogout}
//                   menuItems={[{ label: "Account", onClick: (n) => n("/profile") }]}
//                 />
//               </div>
//             </div>

//             {/* ── Row 2: Name — mobile only ── */}
//             <div className="flex sm:hidden items-center pb-1.5">
//               {(() => {
//                 const displayName =
//                   authUser?.business_name ||
//                   authUser?.contact_person ||
//                   `${authUser?.first_name || ""} ${authUser?.last_name || ""}`;
//                 const isLong = displayName.length > 28;
//                 return isLong ? (
//                   <div className="w-full overflow-hidden">
//                     <style>{`
//                       @keyframes mq { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
//                     `}</style>
//                     <div
//                       className="whitespace-nowrap"
//                       style={{ animation: "mq 8s linear infinite", display: "inline-block" }}
//                     >
//                       <span className="text-[13px] font-semibold text-white/70 pr-10">{displayName}</span>
//                       <span className="text-[13px] font-semibold text-white/70 pr-10">{displayName}</span>
//                     </div>
//                   </div>
//                 ) : (
//                   <span className="text-[13px] font-semibold text-white/70">{displayName}</span>
//                 );
//               })()}
//             </div>

//           </div>
//         </header>
//       )}

//       {/* ================= LAYOUT ================= */}
//       <div className={isRetailerMobile ? "" : "px-3 sm:px-6 lg:px-2"}>
//        <div
//           className={`${
//             isRetailerMobile ? "" : "grid grid-cols-12 gap-3"
//           }`}
//         >
//           {/* {!isNotVerified && !useMobileCustomerUI && (
//             <aside
//               className={`col-span-12 sm:col-span-5 md:col-span-3 lg:col-span-2 sticky top-14 h-[calc(100vh-56px)] overflow-hidden ${
//                 isRetailerMobile
//                   ? "bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl p-3"
//                   : ""
//               }`}
//             >
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )} */}

//           {/* ✅ MOBILE OVERLAY SIDEBAR */}
//           {!isNotVerified && !useMobileCustomerUI && isMobile && (
//             <>
//               {/* BACKDROP */}
//               {sidebarOpen && (
//                 <div
//                   className="fixed inset-0 bg-black/40 z-40"
//                   onClick={() => setSidebarOpen(false)}
//                 />
//               )}

//               {/* SIDEBAR */}
//               <aside
//                 className={`fixed top-0 left-0 h-full w-[260px] bg-white z-50
//                 shadow-xl transition-transform duration-300
//                 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
//               >
//                 <Sidebar
//                   sidebarOpen={sidebarOpen}
//                   setSidebarOpen={setSidebarOpen}
//                 />
//               </aside>
//             </>
//           )}

//           {!isNotVerified && !useMobileCustomerUI && !isMobile && sidebarOpen && (
//             <aside className="col-span-12 sm:col-span-5 md:col-span-4 lg:col-span-2 sticky top-14 max-h-[calc(100vh-20px)] overflow-y-auto">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}


//           {!isNotVerified && useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`${
//               !isNotVerified && sidebarOpen && !isMobile
//                 ? "col-span-12 sm:col-span-7 md:col-span-8 lg:col-span-10"
//                 : "col-span-12"
//             } transition-all`}
//           >
//             <div
//                className={`h-[calc(100vh-20px)] overflow-y-auto ${
//                 isRetailerMobile
//                   ? ""
//                   : "bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100"
//               }`}
//             >
//               {!isNotVerified && !isRetailerMobile && (
//                 <ContentHeader
//                   name={authUser?.business_name}
//                   city={authUser?.city}
//                   email={authUser?.contact_email || authUser?.official_email}
//                   mobile={authUser?.contact_phone || authUser?.official_phone}
//                   loginUserCreated={authUser?.created_at}
//                 />
//               )}

//               <div className={`p-2 ${isRetailerMobile ? "mt-5 pt-16 pb-20" : ""}`}>
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {!isNotVerified && isRetailerMobile && ( Number(authUser?.role) === 5 || Number(authUser?.role) === 6 )&& location.pathname !== "/customer-create" && (
//         <button
//           onClick={() => navigate("/customer-create")}
//           className="fixed bottom-24 right-5 z-50
//                     w-16 h-16
//                     rounded-full
//                     bg-gradient-to-br from-blue-600 to-indigo-700
//                     text-white
//                     flex items-center justify-center
//                     shadow-[0_15px_40px_rgba(31,38,135,0.4)]
//                     hover:scale-110 active:scale-95
//                     transition-all duration-300"
//         >
//           <Plus className="w-8 h-9" />
//         </button>
//       )}

//       {/* ================= FOOTER ================= */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} {authUser?.title}
//       </footer>

//       {/* ================= ZOHO POPUP ================= */}
//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
//             <h2 className="text-lg font-bold">Activate Your Account</h2>
//             <p className="mt-2 text-sm text-slate-600">
//               Activate your account to start using Dashboard.
//             </p>
//            <div className="flex gap-2">
//              <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <button
//             onClick={handleLogout}
//             className=" mt-4 w-full rounded-xl bg-red-600 py-2 text-white font-semibold"
//           >
//             Logout
//           </button>
//            </div>
//           </div>
//         </div>
//       )}

//       {forceLogoutDetected && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
//             <h2 className="text-lg font-bold text-rose-700">
//               Session Terminated
//             </h2>

//             <p className="mt-2 text-sm font-semibold text-slate-600">
//               You have been <span className="font-bold text-rose-600">force logged out</span> by your company.
//               <br />
//               Please contact support for further assistance.
//             </p>

//             <button
//               onClick={handleForceLogout}
//               className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700"
//             >
//               Go to Login
//             </button>
//           </div>
//         </div>
//       )}

//       {showMpinPopup && (
//         <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          
//           <div
//             className={`w-full max-w-sm rounded-[32px] p-6 text-center shadow-2xl border ${
//               isRetailerMobile
//                 ? "bg-white/20 backdrop-blur-3xl border-white/20 text-white"
//                 : "bg-white border-slate-200 text-slate-900"
//             }`}
//           >
            
//             {/* ICON */}
//             <div
//               className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center text-4xl shadow-lg ${
//                 isRetailerMobile
//                   ? "bg-white/20 backdrop-blur-xl border border-white/20"
//                   : "bg-blue-100"
//               }`}
//             >
//               🔐
//             </div>

//             {/* TITLE */}
//             <h2
//               className={`mt-5 text-2xl font-bold ${
//                 isRetailerMobile ? "text-white" : "text-slate-800"
//               }`}
//             >
//               Set Your MPIN
//             </h2>

//             {/* DESCRIPTION */}
//             <p
//               className={`mt-3 text-sm leading-6 ${
//                 isRetailerMobile ? "text-slate-200" : "text-slate-500"
//               }`}
//             >
//               For better security and quick login access,
//               would you like to set your MPIN now?
//             </p>

//             {/* BUTTONS */}
//             <div className="mt-7 flex gap-3">
              
//               <button
//                 onClick={() => setShowMpinPopup(false)}
//                 className={`flex-1 rounded-2xl py-3 font-semibold transition-all ${
//                   isRetailerMobile
//                     ? "bg-white/10 border border-white/20 text-white hover:bg-white/20"
//                     : "border border-slate-300 text-slate-600 hover:bg-slate-100"
//                 }`}
//               >
//                 Later
//               </button>

//               <button
//                 onClick={handleSetMpin}
//                 className={`flex-1 rounded-2xl py-3 font-semibold transition-all ${
//                   isRetailerMobile
//                     ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-[0_10px_30px_rgba(59,130,246,0.45)]"
//                     : "bg-blue-600 text-white hover:bg-blue-700"
//                 }`}
//               >
//                 Yes
//               </button>

//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// ************************************code without port*****************************************************

// import React, { useContext, useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import Logo2 from "../asset/images/rrlogo2.png"
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu, Plus } from "lucide-react";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";

// import { BASE_URL } from "../config/api";
// import { AuthContext } from "../context/AuthContext";
// import axios from "axios";


// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);
//   const [forceLogoutDetected, setForceLogoutDetected] = useState(false);
//   const [checkingLogoutStatus, setCheckingLogoutStatus] = useState(false);


//   const navigate = useNavigate();
//   const location = useLocation();

//   const isCheckoutPage = location.pathname === "/ordersummery";

//   // const authUserString = localStorage.getItem("authUserData");
//   // const authUser = authUserString ? JSON.parse(authUserString) : null;

//   // const [user, setUser] = useState(authUser);

//   const { user, logout, login } = useContext(AuthContext);
//   const authUser = user;


//   // useEffect(() => {
//   //   const syncUser = () => {
//   //     const updated = localStorage.getItem("authUserData");
//   //     if (updated) setUser(JSON.parse(updated));
//   //   };

//   //   window.addEventListener("storage", syncUser);
//   //   return () => window.removeEventListener("storage", syncUser);
//   // }, []);


//   const role = Number(authUser?.role);
//   const isRetailer = role === 5 || role === 6;
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
//   const isRetailerMobile = isRetailer && isMobile;
//   const isCustomerRole = [3, 4, 5, 6, 7].includes(role);

  
//   const useMobileCustomerUI = isCustomerRole && isMobile;

//   /* ---------------- ZOHO ACTIVATION ---------------- */
//   const needsZohoActivation =
//     authUser &&
//     [2,4,5].includes(role) &&
//     Number(authUser.is_verified) === 7 &&
//     !authUser.zoho_id;

//   useEffect(() => {
//     if (needsZohoActivation) setShowZohoPopup(true);
//   }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId =
//         authUser.company_id || 1;

//       const userId = authUser.id;

//       const response = await axios.get(
//         `${BASE_URL}auto-zoho-contact-create`,
//         {
//           params: {
//             company_id: companyId,
//             user_id: userId,
//           },
//         }
//       );

//       const data = response.data;

//       if (!data?.status) {
//         throw new Error(
//           data?.message
//         );
//       }

//       const updatedUser = {
//         ...authUser,
//         zoho_id: data.zoho_id,
//       };

//       login({
//         ...updatedUser,

//         leads:
//           authUser.leads || [],
//       });

//       setShowZohoPopup(false);

//       alert(
//         "🎉 Account activated successfully!"
//       );

//       window.location.reload();

//     } catch (err) {

//       alert(
//         err?.response?.data?.message ||
//         err?.message ||
//         "Failed to activate account."
//       );

//     } finally {

//       setActivating(false);

//     }
//   };

//   /* ---------------- AUTH REDIRECT ---------------- */
//   // useEffect(() => {
//   //   if (!authUser && !["/login", "/customer-login"].includes(location.pathname)) {
//   //     navigate("/login", { replace: true });
//   //   }
//   // }, [authUser, navigate, location.pathname]);

//   /* ---------------- ONBOARDING RULE ---------------- */
//   const requiresOnboard = [2, 4, 5, 6];
//   const isNotVerified =
//     requiresOnboard.includes(role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const onResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//       setIsMobile(window.innerWidth < 640);
//     };
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   useEffect(() => {
//   // ✅ ONLY FOR RETAILER (ROLE = 5)
//   if (
//     !authUser?.id ||
//     ![1, 2, 5, 6].includes(Number(authUser?.role))
//   ) return;

//   let intervalId;

//   const checkLogoutStatus = async () => {
//   try {
//     setCheckingLogoutStatus(true);

//     const response = await axios.get(
//       `${BASE_URL}common/logout-status/${authUser.id}`
//     );

//     const data = response.data;

//     const isForceLogout =
//       data?.is_logout === true ||
//       data?.is_logout === 1;

//     if (
//       data?.status === true &&
//       isForceLogout
//     ) {

//       setForceLogoutDetected(true);

//       clearInterval(intervalId);

//     }

//   } catch (err) {

//     console.error(
//       "Logout status check failed",
//       err
//     );

//   } finally {

//     setCheckingLogoutStatus(false);

//   }
// };

//   // 🔁 initial check
//   checkLogoutStatus();

//   // 🔁 poll every 15 seconds
//   intervalId = setInterval(checkLogoutStatus, 15000);

//   return () => clearInterval(intervalId);
// }, [authUser?.id, authUser?.role]);



//   /* ---------------- LOGOUT ---------------- */
//   const handleLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };

//   const handleForceLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };


//   const roleDisplayName = {
//     1: "Admin",
//     2: "Master Channel Partner",
//     3: "Employee",
//     4: "Channel Partner",
//     5: "Authorized Retail Partner",
//     6: "Promoter",
//     7: "Customer",
//   };

//   const DEFAULT_LOGO = "https://warrantymitra.com/img/logo.png"; 
//   const DEFAULT_LOGO1 = "/images/Logo.png";
//   const DEFAULT_LOGO2 = "/images/white_logo.png";


//   return (
//     <div
//      className={`min-h-screen text-slate-900 relative overflow-hidden z-0 ${
//         isRetailerMobile
//           // ? "bg-gradient-to-br from-[#dbeafe] via-[#f0f9ff] to-[#e0f2fe]"
//           ? "bg-gradient-to-br from-slate-300 via-blue-200 to-slate-500"
//           : "bg-white"
//       }`}
//     >

//       {isRetailerMobile && (
//         <>
//           <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-400/30 blur-[140px] rounded-full pointer-events-none" />
//           <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-indigo-400/30 blur-[140px] rounded-full pointer-events-none" />
//         </>
//       )}

//       {isRetailerMobile && (
//         <div className="fixed top-0 left-0 right-0 z-40 
//                         px-4 py-1 
//                         bg-white/5 backdrop-blur-3xl
//                         border-b border-white/10 shadow-[0_20px_50px_rgba(37,99,235,0.22)]">

//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-slate-700">Hello 👋</p>
//               <h2 className="text-sm font-bold text-slate-900">
//                 {authUser?.business_name}
//               </h2>
//               <h5 className="text-xs font-semibold text-slate-500">
//                 {authUser?.company_code}
//               </h5>
//             </div>

//             {/* <div className="w-10 h-10 rounded-full 
//                             bg-white/20 backdrop-blur-xl 
//                             border border-white/30
//                             flex items-center justify-center">
//               🏪
//             </div> */}

//             <ProfileDropdown
//               avatar={DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//               isRetailerMobile={isRetailerMobile}
//             />
//           </div>
//         </div>
//       )}
//       {/* ================= HEADER ================= */}
//       {!isRetailerMobile && (
//         <header
//         className={`sticky top-0 z-40 ${
//           isRetailerMobile
//             ? "bg-white/20 backdrop-blur-xl border-b border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
//             : "bg-[#104191ff]"
//         }`}
//       >
//         <div className="px-2">
//           <div className="flex h-11 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {!isNotVerified && !useMobileCustomerUI && (
//                 <button
//                   onClick={() => setSidebarOpen((p) => !p)}
//                   className="rounded-xl p-2 hover:bg-white/10"
//                 >
//                   <Menu size={20} className={isRetailerMobile ? "text-slate-800" : "text-white"} />
//                 </button>
//               )}

//               {/* LOGO + NAME */}
//               <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
//                 <img
//                   // src={user?.logo ? `${user.logo}?v=${Date.now()}` : DEFAULT_LOGO}
//                   src={DEFAULT_LOGO2}
//                   className={
//                     user?.logo
//                       ? "h-7 sm:h-8 mx-auto sm:mx-0"
//                       : "h-59 sm:h-8 mx-auto sm:mx-0"
//                   }
//                   alt="Logo"
//                 />

//                 <span className={`text-xs sm:text-sm font-semibold ${
//                     isRetailerMobile ? "text-slate-800" : "text-white/80"
//                   }`}>
//                   {authUser?.contact_person ||
//                     authUser?.business_name ||
//                     `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
//                 </span>
//               </div>
//             </div>

//             <div className="flex gap-2">
              
//               <div className="hidden sm:block px-4 py-3 text-xs text-slate-100">
//                 Logged in as:{" "}
//                 <span className="font-semibold">
//                   {role === 3
//                     ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
//                     : roleDisplayName[role] ?? role}
//                 </span>
//               </div>

//             <ProfileDropdown
//               avatar={ user?.photo_url || DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//             />
//             </div>
//           </div>
//         </div>
//       </header>
//       )}

//       {/* ================= LAYOUT ================= */}
//       <div className={isRetailerMobile ? "" : "px-3 sm:px-6 lg:px-2"}>
//        <div
//           className={`${
//             isRetailerMobile ? "" : "grid grid-cols-12 gap-3"
//           }`}
//         >
//           {/* {!isNotVerified && !useMobileCustomerUI && (
//             <aside
//               className={`col-span-12 sm:col-span-5 md:col-span-3 lg:col-span-2 sticky top-14 h-[calc(100vh-56px)] overflow-hidden ${
//                 isRetailerMobile
//                   ? "bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl p-3"
//                   : ""
//               }`}
//             >
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )} */}

//           {/* ✅ MOBILE OVERLAY SIDEBAR */}
//           {!isNotVerified && !useMobileCustomerUI && isMobile && (
//             <>
//               {/* BACKDROP */}
//               {sidebarOpen && (
//                 <div
//                   className="fixed inset-0 bg-black/40 z-40"
//                   onClick={() => setSidebarOpen(false)}
//                 />
//               )}

//               {/* SIDEBAR */}
//               <aside
//                 className={`fixed top-0 left-0 h-full w-[260px] bg-white z-50
//                 shadow-xl transition-transform duration-300
//                 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
//               >
//                 <Sidebar
//                   sidebarOpen={sidebarOpen}
//                   setSidebarOpen={setSidebarOpen}
//                 />
//               </aside>
//             </>
//           )}

//           {!isNotVerified && !useMobileCustomerUI && !isMobile && sidebarOpen && (
//             <aside className="col-span-12 sm:col-span-5 md:col-span-4 lg:col-span-2 sticky top-14 max-h-[calc(100vh-20px)] overflow-y-auto">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}


//           {!isNotVerified && useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`${
//               !isNotVerified && sidebarOpen && !isMobile
//                 ? "col-span-12 sm:col-span-7 md:col-span-8 lg:col-span-10"
//                 : "col-span-12"
//             } transition-all`}
//           >
//             <div
//                className={`h-[calc(100vh-20px)] overflow-y-auto ${
//                 isRetailerMobile
//                   ? ""
//                   : "bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100"
//               }`}
//             >
//               {!isNotVerified && !isRetailerMobile && (
//                 <ContentHeader
//                   name={authUser?.business_name}
//                   city={authUser?.city}
//                   email={authUser?.contact_email || authUser?.official_email}
//                   mobile={authUser?.contact_phone || authUser?.official_phone}
//                   loginUserCreated={authUser?.created_at}
//                 />
//               )}

//               <div className={`p-2 ${isRetailerMobile ? "mt-5 pt-20 pb-20" : ""}`}>
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {!isNotVerified && isRetailerMobile && ( Number(authUser?.role) === 5 || Number(authUser?.role) === 6 )&& location.pathname !== "/customer-create" && (
//         <button
//           onClick={() => navigate("/customer-create")}
//           className="fixed bottom-24 right-5 z-50
//                     w-16 h-16
//                     rounded-full
//                     bg-gradient-to-br from-blue-600 to-indigo-700
//                     text-white
//                     flex items-center justify-center
//                     shadow-[0_15px_40px_rgba(31,38,135,0.4)]
//                     hover:scale-110 active:scale-95
//                     transition-all duration-300"
//         >
//           <Plus className="w-8 h-9" />
//         </button>
//       )}

//       {/* ================= FOOTER ================= */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} {authUser?.title}
//       </footer>

//       {/* ================= ZOHO POPUP ================= */}
//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
//             <h2 className="text-lg font-bold">Activate Your Account</h2>
//             <p className="mt-2 text-sm text-slate-600">
//               Activate your account to start using Dashboard.
//             </p>
//            <div className="flex gap-2">
//              <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <button
//             onClick={handleLogout}
//             className=" mt-4 w-full rounded-xl bg-red-600 py-2 text-white font-semibold"
//           >
//             Logout
//           </button>
//            </div>
//           </div>
//         </div>
//       )}

//       {forceLogoutDetected && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
//             <h2 className="text-lg font-bold text-rose-700">
//               Session Terminated
//             </h2>

//             <p className="mt-2 text-sm font-semibold text-slate-600">
//               You have been <span className="font-bold text-rose-600">force logged out</span> by your company.
//               <br />
//               Please contact support for further assistance.
//             </p>

//             <button
//               onClick={handleForceLogout}
//               className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700"
//             >
//               Go to Login
//             </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }



// import React, { useContext, useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import Logo2 from "../asset/images/rrlogo2.png"
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";

// import { BASE_URL } from "../config/api";
// import { AuthContext } from "../context/AuthContext";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);
//   const [forceLogoutDetected, setForceLogoutDetected] = useState(false);
//   const [checkingLogoutStatus, setCheckingLogoutStatus] = useState(false);


//   const navigate = useNavigate();
//   const location = useLocation();

//   // const authUserString = localStorage.getItem("authUserData");
//   // const authUser = authUserString ? JSON.parse(authUserString) : null;

//   // const [user, setUser] = useState(authUser);

//   const { user, logout, login } = useContext(AuthContext);
//   const authUser = user;


//   // useEffect(() => {
//   //   const syncUser = () => {
//   //     const updated = localStorage.getItem("authUserData");
//   //     if (updated) setUser(JSON.parse(updated));
//   //   };

//   //   window.addEventListener("storage", syncUser);
//   //   return () => window.removeEventListener("storage", syncUser);
//   // }, []);


//   const role = Number(authUser?.role);
//   const isRetailer = role === 5;
//   const isCustomerRole = [3, 4, 5, 6, 7].includes(role);

//   const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
//   const useMobileCustomerUI = isCustomerRole && isMobile;

//   /* ---------------- ZOHO ACTIVATION ---------------- */
//   const needsZohoActivation =
//     authUser &&
//     [2,4,5].includes(role) &&
//     Number(authUser.is_verified) === 7 &&
//     !authUser.zoho_id;

//   useEffect(() => {
//     if (needsZohoActivation) setShowZohoPopup(true);
//   }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId = authUser.company_id || 1;
//       const userId = authUser.id;

//       const res = await fetch(
//         `${BASE_URL}auto-zoho-contact-create?company_id=${companyId}&user_id=${userId}`
//       );

//       const data = await res.json();
//       if (!data.status) throw new Error(data.message);

//       const updatedUser = { ...authUser, zoho_id: data.zoho_id };
     
//       login({
//         ...authUser,
//         zoho_id: data.zoho_id,
//         leads: authUser.leads || [], // 🔥 PRESERVE LEADS
//       });

//       setShowZohoPopup(false);
//       alert("🎉 Account activated successfully!");
//       window.location.reload();
//     } catch (err) {
//       alert("Failed to activate account.");
//     } finally {
//       setActivating(false);
//     }
//   };

//   /* ---------------- AUTH REDIRECT ---------------- */
//   // useEffect(() => {
//   //   if (!authUser && !["/login", "/customer-login"].includes(location.pathname)) {
//   //     navigate("/login", { replace: true });
//   //   }
//   // }, [authUser, navigate, location.pathname]);

//   /* ---------------- ONBOARDING RULE ---------------- */
//   const requiresOnboard = [2, 4, 5];
//   const isNotVerified =
//     requiresOnboard.includes(role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const onResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//       setIsMobile(window.innerWidth < 640);
//     };
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   useEffect(() => {
//   // ✅ ONLY FOR RETAILER (ROLE = 5)
//   if (
//     !authUser?.id ||
//     ![1, 2, 5].includes(Number(authUser?.role))
//   ) return;

//   let intervalId;

//   const checkLogoutStatus = async () => {
//     try {
//       setCheckingLogoutStatus(true);

//       const res = await fetch(
//         `${BASE_URL}common/logout-status/${authUser.id}`
//       );
//       const data = await res.json();

//       const isForceLogout =
//         data?.is_logout === true || data?.is_logout === 1;

//       if (data?.status === true && isForceLogout) {
//         setForceLogoutDetected(true);
//         clearInterval(intervalId);
//       }
//     } catch (err) {
//       console.error("Logout status check failed", err);
//     } finally {
//       setCheckingLogoutStatus(false);
//     }
//   };

//   // 🔁 initial check
//   checkLogoutStatus();

//   // 🔁 poll every 15 seconds
//   intervalId = setInterval(checkLogoutStatus, 15000);

//   return () => clearInterval(intervalId);
// }, [authUser?.id, authUser?.role]);



//   /* ---------------- LOGOUT ---------------- */
//   const handleLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };

//   const handleForceLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };


//   const roleDisplayName = {
//     1: "Admin",
//     2: "Company",
//     3: "Employee",
//     4: "Agent",
//     5: "Retailer",
//     6: "User",
//     7: "Customer",
//   };

//   const DEFAULT_LOGO = "/images/GoelectroniX.png";
//   const DEFAULT_LOGO1 = "/images/Logo.png";

//   return (
//     <div
//       className={`min-h-screen text-slate-900 relative overflow-hidden ${
//         isRetailer
//           ? "bg-gradient-to-br from-[#dbeafe] via-[#f0f9ff] to-[#e0f2fe]"
//           : "bg-white"
//       }`}
//     >

//       {isRetailer && (
//         <>
//           <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-400/30 blur-[140px] rounded-full" />
//           <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-indigo-400/30 blur-[140px] rounded-full" />
//         </>
//       )}
//       {/* ================= HEADER ================= */}
//       {!isRetailer && (
//         <header
//         className={`sticky top-0 z-40 ${
//           isRetailer
//             ? "bg-white/20 backdrop-blur-xl border-b border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
//             : "bg-[#104191ff]"
//         }`}
//       >
//         <div className="px-2">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {!isNotVerified && !useMobileCustomerUI && (
//                 <button
//                   onClick={() => setSidebarOpen((p) => !p)}
//                   className="rounded-xl p-2 hover:bg-white/10"
//                 >
//                   <Menu size={20} className={isRetailer ? "text-slate-800" : "text-white"} />
//                 </button>
//               )}

//               {/* LOGO + NAME */}
//               <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
//                 <img
//                   src={user?.logo ? `${user.logo}?v=${Date.now()}` : DEFAULT_LOGO}
//                   className={
//                     user?.logo
//                       ? "h-7 sm:h-8 mx-auto sm:mx-0"
//                       : "h-7 sm:h-8 mx-auto sm:mx-0"
//                   }
//                   alt="Logo"
//                 />

//                 <span className={`text-xs sm:text-sm font-semibold ${
//                     isRetailer ? "text-slate-800" : "text-white/80"
//                   }`}>
//                   {authUser?.contact_person ||
//                     authUser?.business_name ||
//                     `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
//                 </span>
//               </div>
//             </div>

//             <div className="flex gap-2">
//               <div className="hidden sm:block px-4 py-3 text-xs text-slate-100">
//                 Logged in as:{" "}
//                 <span className="font-semibold">
//                   {role === 3
//                     ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
//                     : roleDisplayName[role] ?? role}
//                 </span>
//               </div>

//             <ProfileDropdown
//               avatar={ user?.photo_url || user?.logo ||  DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//             />
//             </div>
//           </div>
//         </div>
//       </header>
//       )}

//       {/* ================= LAYOUT ================= */}
//       <div className={isRetailer ? "" : "px-3 sm:px-6 lg:px-2"}>
//        <div
//           className={`grid grid-cols-12 gap-3 ${
//             isRetailer ? "" : "py-3"
//           }`}
//         >
//           {!isNotVerified && !useMobileCustomerUI && (
//             <aside
//               className={`col-span-12 sm:col-span-5 md:col-span-3 lg:col-span-2 ${
//                 isRetailer
//                   ? "bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl p-3"
//                   : ""
//               }`}
//             >
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {!isNotVerified && useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`${
//               !isNotVerified && sidebarOpen
//                 ? "col-span-12 sm:col-span-7 md:col-span-9 lg:col-span-10"
//                 : "col-span-12"
//             } transition-all`}
//           >
//             <div
//               className={`min-h-screen ${
//                 isRetailer
//                   ? ""
//                   : "bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100"
//               }`}
//             >
//               {!isNotVerified && !isRetailer && (
//                 <ContentHeader
//                   name={authUser?.business_name}
//                   city={authUser?.city}
//                   email={authUser?.contact_email || authUser?.official_email}
//                   mobile={authUser?.contact_phone || authUser?.official_phone}
//                   loginUserCreated={authUser?.created_at}
//                 />
//               )}

//               <div className={`p-2 ${useMobileCustomerUI ? "pb-20" : ""}`}>
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {/* ================= FOOTER ================= */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} {authUser?.title}
//       </footer>

//       {/* ================= ZOHO POPUP ================= */}
//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
//             <h2 className="text-lg font-bold">Activate Your Account</h2>
//             <p className="mt-2 text-sm text-slate-600">
//               Activate your account to start using Dashboard.
//             </p>
//            <div className="flex gap-2">
//              <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <button
//             onClick={handleLogout}
//             className=" mt-4 w-full rounded-xl bg-red-600 py-2 text-white font-semibold"
//           >
//             Logout
//           </button>
//            </div>
//           </div>
//         </div>
//       )}

//       {forceLogoutDetected && (
//   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
//     <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
//       <h2 className="text-lg font-bold text-rose-700">
//         Session Terminated
//       </h2>

//       <p className="mt-2 text-sm font-semibold text-slate-600">
//         You have been <span className="font-bold text-rose-600">force logged out</span> by your company.
//         <br />
//         Please contact support for further assistance.
//       </p>

//       <button
//         onClick={handleForceLogout}
//         className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700"
//       >
//         Go to Login
//       </button>
//     </div>
//   </div>
// )}

//     </div>
//   );
// }

// ***********************************************original working *********************************************************

// import React, { useContext, useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import Logo2 from "../asset/images/rrlogo2.png"
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";

// import { BASE_URL } from "../config/api";
// import { AuthContext } from "../context/AuthContext";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);
//   const [forceLogoutDetected, setForceLogoutDetected] = useState(false);
// const [checkingLogoutStatus, setCheckingLogoutStatus] = useState(false);

  

//   const navigate = useNavigate();
//   const location = useLocation();

//   // const authUserString = localStorage.getItem("authUserData");
//   // const authUser = authUserString ? JSON.parse(authUserString) : null;

//   // const [user, setUser] = useState(authUser);

//   const { user, logout, login } = useContext(AuthContext);
//   const authUser = user;



//   // useEffect(() => {
//   //   const syncUser = () => {
//   //     const updated = localStorage.getItem("authUserData");
//   //     if (updated) setUser(JSON.parse(updated));
//   //   };

//   //   window.addEventListener("storage", syncUser);
//   //   return () => window.removeEventListener("storage", syncUser);
//   // }, []);


//   const role = Number(authUser?.role);
//   const isCustomerRole = [3, 4, 5, 6, 7].includes(role);

//   const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
//   const useMobileCustomerUI = isCustomerRole && isMobile;

//   /* ---------------- ZOHO ACTIVATION ---------------- */
//   const needsZohoActivation =
//     authUser &&
//     [2,4,5].includes(role) &&
//     Number(authUser.is_verified) === 7 &&
//     !authUser.zoho_id;

//   useEffect(() => {
//     if (needsZohoActivation) setShowZohoPopup(true);
//   }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId = authUser.company_id || 1;
//       const userId = authUser.id;

//       const res = await fetch(
//         `${BASE_URL}auto-zoho-contact-create?company_id=${companyId}&user_id=${userId}`
//       );

//       const data = await res.json();
//       if (!data.status) throw new Error(data.message);

//       const updatedUser = { ...authUser, zoho_id: data.zoho_id };
     
//       login({
//         ...authUser,
//         zoho_id: data.zoho_id,
//         leads: authUser.leads || [], // 🔥 PRESERVE LEADS
//       });

//       setShowZohoPopup(false);
//       alert("🎉 Account activated successfully!");
//       window.location.reload();
//     } catch (err) {
//       alert("Failed to activate account.");
//     } finally {
//       setActivating(false);
//     }
//   };

//   /* ---------------- AUTH REDIRECT ---------------- */
//   // useEffect(() => {
//   //   if (!authUser && !["/login", "/customer-login"].includes(location.pathname)) {
//   //     navigate("/login", { replace: true });
//   //   }
//   // }, [authUser, navigate, location.pathname]);

//   /* ---------------- ONBOARDING RULE ---------------- */
//   const requiresOnboard = [2, 4, 5];
//   const isNotVerified =
//     requiresOnboard.includes(role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const onResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//       setIsMobile(window.innerWidth < 640);
//     };
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   useEffect(() => {
//   // ✅ ONLY FOR RETAILER (ROLE = 5)
//   if (
//     !authUser?.id ||
//     ![1, 2, 5].includes(Number(authUser?.role))
//   ) return;

//   let intervalId;

//   const checkLogoutStatus = async () => {
//     try {
//       setCheckingLogoutStatus(true);

//       const res = await fetch(
//         `${BASE_URL}common/logout-status/${authUser.id}`
//       );
//       const data = await res.json();

//       const isForceLogout =
//         data?.is_logout === true || data?.is_logout === 1;

//       if (data?.status === true && isForceLogout) {
//         setForceLogoutDetected(true);
//         clearInterval(intervalId);
//       }
//     } catch (err) {
//       console.error("Logout status check failed", err);
//     } finally {
//       setCheckingLogoutStatus(false);
//     }
//   };

//   // 🔁 initial check
//   checkLogoutStatus();

//   // 🔁 poll every 15 seconds
//   intervalId = setInterval(checkLogoutStatus, 15000);

//   return () => clearInterval(intervalId);
// }, [authUser?.id, authUser?.role]);



//   /* ---------------- LOGOUT ---------------- */
//   const handleLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };

//   const handleForceLogout = () => {
//   logout();
//   navigate("/login", { replace: true });
// };


//   const roleDisplayName = {
//     1: "Admin",
//     2: "Company",
//     3: "Employee",
//     4: "Agent",
//     5: "Retailer",
//     6: "User",
//     7: "Customer",
//   };

//   const DEFAULT_LOGO = "/images/GoelectroniX.png";
//   const DEFAULT_LOGO1 = "/images/Logo.png";

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* ================= HEADER ================= */}
//       <header className="sticky top-0 z-40 bg-[#104191ff] shadow-sm">
//         <div className="px-2">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {!isNotVerified && !useMobileCustomerUI && (
//                 <button
//                   onClick={() => setSidebarOpen((p) => !p)}
//                   className="rounded-xl p-2 hover:bg-white/10"
//                 >
//                   <Menu size={20} className="text-white" />
//                 </button>
//               )}

//               {/* LOGO + NAME */}
//               <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
//                 <img
//                   src={user?.logo ? `${user.logo}?v=${Date.now()}` : DEFAULT_LOGO}
//                   className={
//                     user?.logo
//                       ? "h-7 sm:h-8 mx-auto sm:mx-0"
//                       : "h-7 sm:h-8 mx-auto sm:mx-0"
//                   }
//                   alt="Logo"
//                 />

//                 <span className="text-xs sm:text-sm font-semibold text-white/80 text-center sm:text-left">
//                   {authUser?.contact_person ||
//                     authUser?.business_name ||
//                     `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
//                 </span>
//               </div>
//             </div>

//             <div className="flex gap-2">
//               <div className="hidden sm:block px-4 py-3 text-xs text-slate-100">
//                 Logged in as:{" "}
//                 <span className="font-semibold">
//                   {role === 3
//                     ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
//                     : roleDisplayName[role] ?? role}
//                 </span>
//               </div>

//             <ProfileDropdown
//               avatar={ user?.photo_url || user?.logo ||  DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//             />
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* ================= LAYOUT ================= */}
//       <div className="px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12  gap-3 py-3">
//           {!isNotVerified && !useMobileCustomerUI && (
//             <aside className="col-span-12 sm:col-span-5 md:col-span-3 lg:col-span-2">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {!isNotVerified && useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`${
//               !isNotVerified && sidebarOpen
//                 ? "col-span-12 sm:col-span-7 md:col-span-9 lg:col-span-10"
//                 : "col-span-12"
//             } transition-all`}
//           >
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100">
//               {!isNotVerified && (
//                 <ContentHeader
//                   name={authUser?.business_name}
//                   city={authUser?.city}
//                   email={authUser?.contact_email || authUser?.official_email}
//                   mobile={authUser?.contact_phone || authUser?.official_phone}
//                   loginUserCreated={authUser?.created_at}
//                 />
//               )}

//               <div className={`p-2 ${useMobileCustomerUI ? "pb-20" : ""}`}>
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {/* ================= FOOTER ================= */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} {authUser?.title}
//       </footer>

//       {/* ================= ZOHO POPUP ================= */}
//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
//             <h2 className="text-lg font-bold">Activate Your Account</h2>
//             <p className="mt-2 text-sm text-slate-600">
//               Activate your account to start using Dashboard.
//             </p>
//            <div className="flex gap-2">
//              <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <button
//             onClick={handleLogout}
//             className=" mt-4 w-full rounded-xl bg-red-600 py-2 text-white font-semibold"
//           >
//             Logout
//           </button>
//            </div>
//           </div>
//         </div>
//       )}

//       {forceLogoutDetected && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
//             <h2 className="text-lg font-bold text-rose-700">
//               Session Terminated
//             </h2>

//             <p className="mt-2 text-sm font-semibold text-slate-600">
//               You have been <span className="font-bold text-rose-600">force logged out</span> by your company.
//               <br />
//               Please contact support for further assistance.
//             </p>

//             <button
//               onClick={handleForceLogout}
//               className="mt-6 w-full rounded-xl bg-rose-600 py-2 text-white font-semibold hover:bg-rose-700"
//             >
//               Go to Login
//             </button>
//           </div>
//         </div>
//       )}

//     </div>
//   );
// }


// import React, { useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import Logo2 from "../asset/images/rrlogo2.png"
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";

// import { BASE_URL } from "../config/api";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);
  

//   const navigate = useNavigate();
//   const location = useLocation();

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   const [user, setUser] = useState(authUser);

//   useEffect(() => {
//     const syncUser = () => {
//       const updated = localStorage.getItem("authUserData");
//       if (updated) setUser(JSON.parse(updated));
//     };

//     window.addEventListener("storage", syncUser);
//     return () => window.removeEventListener("storage", syncUser);
//   }, []);


//   const role = Number(authUser?.role);
//   const isCustomerRole = [4, 5, 6, 7].includes(role);

//   const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
//   const useMobileCustomerUI = isCustomerRole && isMobile;

//   /* ---------------- ZOHO ACTIVATION ---------------- */
//   const needsZohoActivation =
//     authUser &&
//     [2,4,5].includes(role) &&
//     Number(authUser.is_verified) === 7 &&
//     !authUser.zoho_id;

//   useEffect(() => {
//     if (needsZohoActivation) setShowZohoPopup(true);
//   }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId = authUser.company_id || 1;
//       const userId = authUser.id;

//       const res = await fetch(
//         `${BASE_URL}auto-zoho-contact-create?company_id=${companyId}&user_id=${userId}`
//       );

//       const data = await res.json();
//       if (!data.status) throw new Error(data.message);

//       const updatedUser = { ...authUser, zoho_id: data.zoho_id };
//       localStorage.setItem("authUserData", JSON.stringify(updatedUser));

//       setShowZohoPopup(false);
//       alert("🎉 Account activated successfully!");
//       window.location.reload();
//     } catch (err) {
//       alert("Failed to activate account.");
//     } finally {
//       setActivating(false);
//     }
//   };

//   /* ---------------- AUTH REDIRECT ---------------- */
//   useEffect(() => {
//     if (!authUser && !["/login", "/customer-login"].includes(location.pathname)) {
//       navigate("/login", { replace: true });
//     }
//   }, [authUser, navigate, location.pathname]);

//   /* ---------------- ONBOARDING RULE ---------------- */
//   const requiresOnboard = [2, 4, 5];
//   const isNotVerified =
//     requiresOnboard.includes(role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const onResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//       setIsMobile(window.innerWidth < 640);
//     };
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   /* ---------------- LOGOUT ---------------- */
//   const handleLogout = () => {
//     localStorage.clear();
//     localStorage.clear();
//     navigate(role === 7 ? "/customer-login" : "/login", { replace: true });
//     window.location.reload();
//   };

//   const roleDisplayName = {
//     1: "Admin",
//     2: "Company",
//     3: "Employee",
//     4: "Agent",
//     5: "Retailer",
//     6: "User",
//     7: "Customer",
//   };

//   const DEFAULT_LOGO = "/images/GoelectroniX.png";
//   const DEFAULT_LOGO1 = "/images/Logo.png";

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* ================= HEADER ================= */}
//       <header className="sticky top-0 z-40 bg-[#104191ff] shadow-sm">
//         <div className="px-2">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {!isNotVerified && !useMobileCustomerUI && (
//                 <button
//                   onClick={() => setSidebarOpen((p) => !p)}
//                   className="rounded-xl p-2 hover:bg-white/10"
//                 >
//                   <Menu size={20} className="text-white" />
//                 </button>
//               )}

//               {/* LOGO + NAME */}
//               <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
//                 <img
//                   src={user?.logo ? `${user.logo}?v=${Date.now()}` : DEFAULT_LOGO}
//                   className={
//                     user?.logo
//                       ? "h-20 sm:h-24 mx-auto sm:mx-0"
//                       : "h-7 sm:h-8 mx-auto sm:mx-0"
//                   }
//                   alt="Logo"
//                 />



//                 <span className="text-xs sm:text-sm font-semibold text-white/80 text-center sm:text-left">
//                   {authUser?.contact_person ||
//                     authUser?.business_name ||
//                     `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
//                 </span>
//               </div>
//             </div>

//             <div className="flex gap-2">
//               <div className="hidden sm:block px-4 py-3 text-xs text-slate-100">
//                 Logged in as:{" "}
//                 <span className="font-semibold">
//                   {role === 3
//                     ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
//                     : roleDisplayName[role] ?? role}
//                 </span>
//               </div>

//             <ProfileDropdown
//               avatar={user?.logo || DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//             />
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* ================= LAYOUT ================= */}
//       <div className="px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12  gap-3 py-3">
//           {!isNotVerified && !useMobileCustomerUI && (
//             <aside className="col-span-12 sm:col-span-5 md:col-span-3 lg:col-span-2">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {!isNotVerified && useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`${
//               !isNotVerified && sidebarOpen
//                 ? "col-span-12 sm:col-span-7 md:col-span-9 lg:col-span-10"
//                 : "col-span-12"
//             } transition-all`}
//           >
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100">
//               {!isNotVerified && (
//                 <ContentHeader
//                   name={authUser?.business_name}
//                   city={authUser?.city}
//                   email={authUser?.contact_email || authUser?.official_email}
//                   mobile={authUser?.contact_phone || authUser?.official_phone}
//                   loginUserCreated={authUser?.created_at}
//                 />
//               )}

//               <div className={`p-2 ${useMobileCustomerUI ? "pb-20" : ""}`}>
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {/* ================= FOOTER ================= */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} {authUser?.title}
//       </footer>

//       {/* ================= ZOHO POPUP ================= */}
//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
//             <h2 className="text-lg font-bold">Activate Your Account</h2>
//             <p className="mt-2 text-sm text-slate-600">
//               Activate your account to start using Dashboard.
//             </p>
//            <div className="flex gap-2">
//              <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <button
//             onClick={handleLogout}
//             className=" mt-4 w-full rounded-xl bg-red-600 py-2 text-white font-semibold"
//           >
//             Logout
//           </button>
//            </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }



// import React, { useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import Logo2 from "../asset/images/rrlogo2.png"
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";

// import { BASE_URL } from "../config/api";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);

//   const navigate = useNavigate();
//   const location = useLocation();

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   const role = Number(authUser?.role);
//   const isCustomerRole = [4, 5, 6, 7].includes(role);

//   const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
//   const useMobileCustomerUI = isCustomerRole && isMobile;

//   /* ---------------- ZOHO ACTIVATION ---------------- */
//   const needsZohoActivation =
//     authUser &&
//     [2,4,5].includes(role) &&
//     Number(authUser.is_verified) === 7 &&
//     !authUser.zoho_id;

//   useEffect(() => {
//     if (needsZohoActivation) setShowZohoPopup(true);
//   }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId = authUser.company_id || 1;
//       const userId = authUser.id;

//       const res = await fetch(
//         `${BASE_URL}auto-zoho-contact-create?company_id=${companyId}&user_id=${userId}`
//       );

//       const data = await res.json();
//       if (!data.status) throw new Error(data.message);

//       const updatedUser = { ...authUser, zoho_id: data.zoho_id };
//       localStorage.setItem("authUserData", JSON.stringify(updatedUser));

//       setShowZohoPopup(false);
//       alert("🎉 Account activated successfully!");
//       window.location.reload();
//     } catch (err) {
//       alert("Failed to activate account.");
//     } finally {
//       setActivating(false);
//     }
//   };

//   /* ---------------- AUTH REDIRECT ---------------- */
//   useEffect(() => {
//     if (!authUser && !["/login", "/customer-login"].includes(location.pathname)) {
//       navigate("/login", { replace: true });
//     }
//   }, [authUser, navigate, location.pathname]);

//   /* ---------------- ONBOARDING RULE ---------------- */
//   const requiresOnboard = [2, 4, 5];
//   const isNotVerified =
//     requiresOnboard.includes(role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   /* ---------------- RESPONSIVE ---------------- */
//   useEffect(() => {
//     const onResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//       setIsMobile(window.innerWidth < 640);
//     };
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   /* ---------------- LOGOUT ---------------- */
//   const handleLogout = () => {
//     localStorage.clear();
//     localStorage.clear();
//     navigate(role === 7 ? "/customer-login" : "/login", { replace: true });
//     window.location.reload();
//   };

//   const roleDisplayName = {
//     1: "Admin",
//     2: "Company",
//     3: "Employee",
//     4: "Agent",
//     5: "Retailer",
//     6: "User",
//     7: "Customer",
//   };

//   const DEFAULT_LOGO = "/images/GoelectroniX.png";
//   const DEFAULT_LOGO1 = "/images/Logo.png";

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* ================= HEADER ================= */}
//       <header className="sticky top-0 z-40 bg-[#104191ff] shadow-sm">
//         <div className="px-4">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {!isNotVerified && !useMobileCustomerUI && (
//                 <button
//                   onClick={() => setSidebarOpen((p) => !p)}
//                   className="rounded-xl p-2 hover:bg-white/10"
//                 >
//                   <Menu size={20} className="text-white" />
//                 </button>
//               )}

//               {/* LOGO + NAME */}
//               <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
//                 <img
//                   src={authUser?.logo || DEFAULT_LOGO}
//                   alt="Logo"
//                   className="h-7 sm:h-8 mx-auto sm:mx-0"
//                   onError={(e) => {
//                     e.currentTarget.src = DEFAULT_LOGO;
//                   }}
//                 />


//                 <span className="text-xs sm:text-sm font-semibold text-white/80 text-center sm:text-left">
//                   {authUser?.contact_person ||
//                     authUser?.business_name ||
//                     `${authUser?.first_name || ""} ${authUser?.last_name || ""}`}
//                 </span>
//               </div>
//             </div>

//             <div className="flex gap-2">
//               <div className="hidden sm:block px-4 py-3 text-xs text-slate-100">
//                 Logged in as:{" "}
//                 <span className="font-semibold">
//                   {role === 3
//                     ? `${roleDisplayName[role]} (${authUser?.type_of_user || "Employee"} ${authUser?.position || "-"})`
//                     : roleDisplayName[role] ?? role}
//                 </span>
//               </div>

//             <ProfileDropdown
//               avatar={authUser?.logo || DEFAULT_LOGO1}
//               userCode={authUser?.company_code || authUser?.employee_id || authUser?.id }
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (n) => n("/profile") },
//                 // { label: "Settings", onClick: (n) => n("/settings") },
//               ]}
//             />
//             </div>
//           </div>
//         </div>
//       </header>

//       {/* ================= LAYOUT ================= */}
//       <div className="px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12 gap-3 py-3">
//           {!isNotVerified && !useMobileCustomerUI && (
//             <aside className="col-span-12 sm:col-span-4 lg:col-span-2">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`${!isNotVerified && sidebarOpen
//               ? "col-span-12 sm:col-span-8 lg:col-span-10"
//               : "col-span-12"
//             } transition-all`}
//           >
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100">
//               {!isNotVerified && (
//                 <ContentHeader
//                   name={authUser?.business_name}
//                   city={authUser?.city}
//                   email={authUser?.contact_email || authUser?.official_email}
//                   mobile={authUser?.contact_phone || authUser?.official_phone}
//                   loginUserCreated={authUser?.created_at}
//                 />
//               )}

//               <div className={`p-2 ${useMobileCustomerUI ? "pb-24" : ""}`}>
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {/* ================= FOOTER ================= */}
//       <footer className="border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>

//       {/* ================= ZOHO POPUP ================= */}
//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center shadow-xl">
//             <h2 className="text-lg font-bold">Activate Your Account</h2>
//             <p className="mt-2 text-sm text-slate-600">
//               Activate your account to start using invoices and payments.
//             </p>
//             <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 py-2 text-white font-semibold"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <button
//             onClick={handleLogout}
//             className="w-full text-left px-4 py-2 text-sm bg-red-600 text-red-300 hover:bg-red-100"
//           >
//             Logout
//           </button>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }



// import React, { useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";
// import { BASE_URL } from "../config/api";
// import MobileBottomNav from "../Component/Common/MobileBottomNav";
// import MobilePhoneCanvas from "../Component/Common/MobilePhoneCanvas";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);

//   const [showZohoPopup, setShowZohoPopup] = useState(false);
//   const [activating, setActivating] = useState(false);

//   const navigate = useNavigate();
//   const location = useLocation();

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const ID = authUser.id;
//   const CompanyID = authUser.company_id;
//   const needsZohoActivation =
//   authUser &&
//   [2, 5].includes(Number(authUser.role)) && // ✅ only Company & Retailer
//   Number(authUser.is_verified) === 7 &&
//   !authUser.zoho_id;



//   useEffect(() => {
//   if (needsZohoActivation) {
//     setShowZohoPopup(true);
//   }
// }, [needsZohoActivation]);

//   const activateZohoAccount = async () => {
//     try {
//       setActivating(true);

//       const companyId = authUser.company_id || authUser.id;
//       const userId = authUser.id;

//       const res = await fetch(
//         `https://zoho.goelectronix.in/api/auto-zoho-contact-create?company_id=${companyId}&user_id=${userId}`,
//         {
//           method: "GET",
//           headers: {
//             "Content-Type": "application/json",
//           },
//         }
//       );

//       const data = await res.json();

//       if (!data.status) {
//         throw new Error(data.message || "Zoho activation failed");
//       }

//       // ✅ Update session with zoho_id
//       const updatedUser = {
//         ...authUser,
//         zoho_id: data.zoho_id,
//       };

//       localStorage.setItem("authUserData", JSON.stringify(updatedUser));

//       setShowZohoPopup(false);

//       alert("🎉 Your account has been activated successfully!");

//       // 🔄 Reload to re-evaluate routes & permissions
//       window.location.reload();

//     } catch (err) {
//       console.error("Zoho activation error:", err);
//       alert("Failed to activate account. Please try again.");
//     } finally {
//       setActivating(false);
//     }
//   };


//   // ------------------------------
//   // 1️⃣ Redirect if NOT logged in
//   // ------------------------------
//   useEffect(() => {
//   if (!authUser && location.pathname !== "/login" && location.pathname !== "/customer-login") {
//     navigate("/login", { replace: true });
//   }
// }, [authUser, navigate, location.pathname]);


//   // ------------------------------
//   // 2️⃣ Role-based onboarding rule
//   // ------------------------------
//   const requiresOnboard = [2, 4, 5];
//   const isNotVerified =
//     requiresOnboard.includes(authUser?.role) &&
//     (authUser?.is_verified === null || authUser?.is_verified < 7);

//   // If user requires onboarding and isn't on onboarding page → redirect
//   useEffect(() => {
//     if (isNotVerified && location.pathname !== "/onboard") {
//       navigate("/onboard", { replace: true });
//     }
//   }, [isNotVerified, navigate, location.pathname]);

//   // ------------------------------
//   // Sidebar responsive toggle
//   // ------------------------------
//   useEffect(() => {
//     const handleResize = () => setSidebarOpen(window.innerWidth >= 640);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // ------------------------------
//   // Logout Handler
//   // ------------------------------
//   const handleLogout = () => {
//     const authUser = JSON.parse(localStorage.getItem("authUserData") || "null");

//     const isCustomer = Number(authUser?.role) === 7;

//     localStorage.clear();
//     localStorage.clear();

//     navigate(isCustomer ? "/customer-login" : "/login", { replace: true });

//     // optional but safe to fully reset app state
//     window.location.reload();
//   };


//   const role = Number(authUser?.role);
// const isCustomerRole = [5, 6, 7].includes(role);

// const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

// useEffect(() => {
//   const onResize = () => setIsMobile(window.innerWidth < 640);
//   window.addEventListener("resize", onResize);
//   return () => window.removeEventListener("resize", onResize);
// }, []);

// const useMobileCustomerUI = isCustomerRole && isMobile;


//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Hide menu button ON onboarding page */}
//               {!isNotVerified && (
//                 <button
//                   onClick={() => setSidebarOpen((prev) => !prev)}
//                   className="rounded-xl p-2 hover:bg-white/10 focus:outline-none"
//                 >
//                   <Menu className="text-white" size={20} />
//                 </button>
//               )}
//               <div className="flex items-center gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">
//                   {authUser?.contact_person || authUser?.business_name || (`${authUser?.first_name} ${authUser?.last_name}`) || "-"}
//                 </span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={authUser?.id}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/profile") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3">
//           {!isNotVerified && !useMobileCustomerUI && (
//             <aside className="col-span-12 sm:col-span-4 lg:col-span-2">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {useMobileCustomerUI && <MobileBottomNav />}

//           <main
//             className={`
//               ${!isNotVerified && !useMobileCustomerUI && sidebarOpen
//                 ? "col-span-12 sm:col-span-8 lg:col-span-10"
//                 : "col-span-12"}
//               h-screen overflow-y-auto
//             `}
//           >
//             <div
//               className="
//                 min-h-screen

//                 /* MOBILE + TABLET GLASS */
//                 sm:bg-white/40
//                 sm:backdrop-blur-2xl
//                 sm:bg-gradient-to-br
//                 sm:from-blue-100/60
//                 sm:via-white/50
//                 sm:to-slate-100/60

//                 /* DESKTOP RESET */
//                 md:bg-gradient-to-br
//                 md:from-slate-100
//                 md:via-blue-50
//                 md:to-slate-100
//                 md:backdrop-blur-0
//               "
//             >

//               {/* Hide detail header when onboarding */}
//               {!isNotVerified && (
//                 <ContentHeader
//                   name={authUser?.business_name ||(`${authUser.first_name} ${authUser.last_name}`) || "-"}
//                   city={authUser?.city || (`${authUser.first_name} ${authUser.last_name}`) || "N/A"}
//                   joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//                   email={authUser?.contact_email || authUser?.official_email || "-"}
//                   mobile={authUser?.contact_phone || authUser?.official_phone || "-"}
//                   loginUserCreated={authUser?.created_at || "-"}
//                 />
//               )}

//               {useMobileCustomerUI ? (
//                 <MobilePhoneCanvas>
//                   <div className="pb-24">
//                     <Outlet />
//                   </div>
//                 </MobilePhoneCanvas>
//               ) : (
//                 <div className="p-2">
//                   <Outlet />
//                 </div>
//               )}

//             </div>
//           </main>
//         </div>
//       </div>

//       <footer className="mt-4 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>

//       {showZohoPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
//           <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md text-center">
//             <h2 className="text-lg font-bold text-slate-900">
//               Activate Your Account
//             </h2>

//             <p className="mt-2 text-sm text-slate-600">
//               Your onboarding is complete.  
//               Activate your account to start using invoices, payments.
//             </p>

//             <button
//               onClick={activateZohoAccount}
//               disabled={activating}
//               className="mt-4 w-full rounded-xl bg-blue-600 px-4 py-2 text-white font-semibold hover:bg-blue-700 disabled:opacity-70"
//             >
//               {activating ? "Activating..." : "Activate Now"}
//             </button>

//             <p className="mt-3 text-xs text-slate-500">
//               This is a one-time process.
//             </p>
//           </div>
//         </div>
//       )}

//     </div>
//   );
// }


// import React, { useEffect, useState } from "react";
// import { useNavigate, Outlet } from "react-router-dom";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const navigate = useNavigate();

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   // Redirect to login if not logged in
//   useEffect(() => {
//     if (!authUser) navigate("/login");
//   }, [authUser, navigate]);

//   // Roles 2,4,5 → force onboarding if not verified
//   const requiresOnboard = [2, 4, 5];
//   const isNotVerified = requiresOnboard.includes(authUser?.role) && authUser?.is_verified === null;

//   useEffect(() => {
//     if (isNotVerified) navigate("/onboard", { replace: true });
//   }, [isNotVerified, navigate]);

//   // Handle window resize
//   useEffect(() => {
//     const handleResize = () => setSidebarOpen(window.innerWidth >= 640);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {!isNotVerified && (
//                 <button
//                   onClick={() => setSidebarOpen((prev) => !prev)}
//                   className="rounded-xl p-2 hover:bg-white/10 focus:outline-none"
//                 >
//                   <Menu className="text-white" size={20} />
//                 </button>
//               )}
//               <div className="flex items-center gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">
//                   {authUser?.contact_person || "-"}
//                 </span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={authUser?.id}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3">
//           {!isNotVerified && (
//             <aside
//               className={`${sidebarOpen ? "block" : "hidden"} col-span-12 sm:col-span-4 lg:col-span-2 transition-all duration-300`}
//             >
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

          // <main
          //   className={`${!isNotVerified && sidebarOpen
          //     ? "col-span-12 sm:col-span-8 lg:col-span-10"
          //     : "col-span-12"
          //   } transition-all`}
          // >
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100">
//               {!isNotVerified && (
//                 <ContentHeader
//                   name={authUser?.business_name || "-"}
//                   city={authUser?.city || "N/A"}
//                   joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//                   email={authUser?.contact_email || "-"}
//                   mobile={authUser?.contact_phone || "-"}
//                   loginUserCreated={authUser?.created_at || "-"}
//                 />
//               )}
//               <div className="p-2">
//                 {/* For admins, you can render the admin dashboard */}
//                 {authUser?.role === 1 ? (
//                   <Outlet /> // Admin dashboard routes/components here
//                 ) : (
//                   <Outlet /> // Other users' content
//                 )}
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {/* Footer */}
//       <footer className="mt-4 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>
//     </div>
//   );
// }




// import { useEffect, useState } from "react";
// import { useNavigate, Outlet } from "react-router-dom";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     const handleResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   // ------------------------------------------------------
//   // NEW LOGIC: If is_verified = null → force onboarding page
//   // ------------------------------------------------------
//   const isNotVerified = authUser?.is_verified === null;

//   useEffect(() => {
//     if (isNotVerified) {
//       navigate("/onboard");
//     }
//   }, [isNotVerified, navigate]);

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Header ALWAYS visible */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Hide sidebar toggle when user is not verified */}
//               {!isNotVerified && (
//                 <button
//                   onClick={() => setSidebarOpen((prev) => !prev)}
//                   className="rounded-xl p-2 hover:bg-white/10 focus:outline-none"
//                 >
//                   <Menu className="text-white" size={20} />
//                 </button>
//               )}

//               <div className="flex items-center gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">
//                   {authUser?.name}
//                 </span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={authUser?.code}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3">

//           {/* Sidebar — HIDDEN only if NOT verified */}
//           {!isNotVerified && (
//             <aside
//               className={`${sidebarOpen ? "block" : "hidden"} col-span-12 sm:col-span-4 lg:col-span-2 transition-all duration-300`}
//             >
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {/* Main Content */}
//           <main
//             className={`${!isNotVerified && sidebarOpen
//               ? "col-span-12 sm:col-span-8 lg:col-span-10"
//               : "col-span-12"
//             } transition-all`}
//           >
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100">

//               {/* Hide ContentHeader when not verified */}
//               {!isNotVerified && (
//                 <ContentHeader
//                   name={authUser?.business_name || "-"}
//                   city={authUser?.city || "N/A"}
//                   joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//                   email={authUser?.email || "-"}
//                   mobile={authUser?.mobile || "-"}
//                   loginUserCreated={authUser?.created_at || "-"}
//                 />
//               )}

//               <div className="p-2">
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       {/* Footer ALWAYS visible */}
//       <footer className="mt-4 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>
//     </div>
//   );
// }




// ******************************************main code below*****************************************************

// import { useEffect, useState } from "react";
// import { useNavigate, Outlet } from "react-router-dom";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     const handleResize = () => {
//       setSidebarOpen(window.innerWidth >= 640);
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Show Menu button only on small devices */}
//               <button
//                 onClick={() => setSidebarOpen((prev) => !prev)}
//                 className="rounded-xl p-2 hover:bg-white/10 focus:outline-none"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>

//               <div className="flex items-center gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">
//                   {authUser?.business_name}
//                 </span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={authUser?.org_code}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Main Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-2">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3">
          
//           {/* Sidebar */}
//           <aside
//             className={`${sidebarOpen ? "block" : "hidden"} col-span-12 sm:col-span-4 lg:col-span-2 transition-all duration-300`}
//           >
//             <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//           </aside>

//           {/* Main Content */}
//           <main
//             className={`${sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"} transition-all`}
//           >
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100">
//               <ContentHeader
//                 name={authUser?.business_name || "-"}
//                 city={authUser?.city || "N/A"}
//                 joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//                 email={authUser?.email || "-"}
//                 mobile={authUser?.mobile || "-"}
//                 loginUserCreated={authUser?.created_at || "-"}
//               />
//               <div className="p-2">
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       <footer className="mt-4 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>
//     </div>
//   );
// }

// ******************************************main code above*************************************************

// import { useEffect, useState } from "react";
// import { useNavigate, Outlet } from "react-router-dom";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640); // open by default on large screens
//   const navigate = useNavigate();

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     const handleResize = () => {
//       if (window.innerWidth >= 640) setSidebarOpen(true); // always open on large screens
//       else setSidebarOpen(false); // auto close on small screens
//     };

//     window.addEventListener("resize", handleResize);
//     handleResize();
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const LoginUserName = authUser?.business_name;
//   const loginUserCode = authUser?.org_code;

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Menu button for small screens only */}
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10 focus:outline-none sm:hidden"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>

//               <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">{LoginUserName}</span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={loginUserCode}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Layout */}
//       <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar */}
//           {/* {sidebarOpen && ( */}
//             <aside className="col-span-12 sm:col-span-4 lg:col-span-2">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           {/* )} */}

//           {/* Content */}
//           <main className={sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"}>
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 text-slate-900">
//               <ContentHeader
//                 name={authUser?.business_name || "-"}
//                 city={authUser?.city || "N/A"}
//                 joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//                 email={authUser?.email || "-"}
//                 mobile={authUser?.mobile || "-"}
//                 loginUserCreated={authUser?.created_at || "-"}
//               />
//               <div className="p-3">
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>
//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import { useNavigate, Outlet } from "react-router-dom";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";
// import { Menu } from "lucide-react";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     const handleResize = () => setSidebarOpen(window.innerWidth >= 640);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const LoginUserName = authUser?.business_name;
//   const loginUserCode = authUser?.org_code;

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Menu button for small screens */}
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10 focus:outline-none sm:hidden md:hidden"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>

//               <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">{LoginUserName}</span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={loginUserCode}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Layout */}
//       <div className="mx-auto  px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar */}
//           {sidebarOpen && (
//             <aside className="col-span-12 sm:col-span-4 lg:col-span-2">
//               <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
//             </aside>
//           )}

//           {/* Content */}
//           <main className={sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"}>
//             <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 text-slate-900">
//               <ContentHeader
//                 name={authUser?.business_name || "-"}
//                 city={authUser?.city || "N/A"}
//                 joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//                 email={authUser?.email || "-"}
//                 mobile={authUser?.mobile || "-"}
//                 loginUserCreated={authUser?.created_at || "-"}
//               />
//               <div className="p-3">
//                 <Outlet />
//               </div>
//             </div>
//           </main>
//         </div>
//       </div>

//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>
//     </div>
//   );
// }



// import { useEffect, useState } from "react";
// import { useNavigate, NavLink, Outlet } from "react-router-dom";
// import { Menu, ChevronDown, LayoutDashboard, Users, KeyRound, Package, ClipboardList, FileText, Ticket, Settings } from "lucide-react";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import ContentHeader from "../Component/Common/ContentHeader";
// import Sidebar from "../Component/Common/Sidebar";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 640);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     const handleResize = () => setSidebarOpen(window.innerWidth >= 640);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const LoginUserName = authUser?.business_name;
//   const loginUserCode = authUser?.org_code;

//   const nav = [
//     { path: "/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
//     { path: "/allretailers", icon: <Users size={18} />, label: "Retailers" },
//     { path: "/keys", icon: <KeyRound size={18} />, label: "Keys" },
//     { path: "/inventory", icon: <Package size={18} />, label: "Inventory" },
//     { path: "/orders", icon: <ClipboardList size={18} />, label: "Orders" },
//     { path: "/invoices", icon: <FileText size={18} />, label: "Invoices" },
//     { path: "/support", icon: <Ticket size={18} />, label: "Support" },
//     { path: "/account", icon: <Settings size={18} />, label: "Account" },
//   ];

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Menu button for small screens */}
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10 focus:outline-none sm:hidden"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>

//               <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">{LoginUserName}</span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={loginUserCode}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Layout */}
//       <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        // <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
        //   {/* Sidebar */}
        //   <Sidebar sidebarOpen={sidebarOpen} />

        //   {/* Content */}
        //   <main className={sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"}>
        //     <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 text-slate-900">
        //       <ContentHeader
        //       name={authUser?.business_name || "-"}
        //       city={authUser?.city || "N/A"}
        //       joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
        //       email={authUser?.email || "-"}
        //       mobile={authUser?.mobile || "-"}
        //       loginUserCreated={authUser?.created_at || "-"}
        //     />
        //       <Outlet />
        //     </div>
        //   </main>
        // </div>
//       </div>

//       {/* Bottom Navigation for small screens */}
      
        
    

//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Hub Panel
//       </footer>
//     </div>
//   );
// }



// import { useEffect, useState } from "react";
// import { useNavigate, Outlet, useLocation } from "react-router-dom";
// import { Menu } from "lucide-react";
// import LogoImg from "../asset/images/GoelectroniX.png";

// import navBarData from "../Routes/navBarData";
// import ProfileDropdown from "../Component/Common/ProfileDropdown";
// import Sidebar from "../Component/Common/Sidebar";
// import ContentHeader from "../Component/Common/ContentHeader";

// export default function HubPanel() {
//   const [sidebarOpen, setSidebarOpen] = useState(true);
//   const navigate = useNavigate();

  

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     const handleResize = () => {
//       // Auto close sidebar on small screens
//       if (window.innerWidth < 640) setSidebarOpen(false);
//       else setSidebarOpen(true);
//     };

//     window.addEventListener("resize", handleResize);
//     handleResize();
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const LoginUserName = authUser?.business_name;
//   const loginUserCode = authUser?.org_code;

//   return (
//     <div className="min-h-screen flex flex-col bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm bg-[#104191ff]">
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Menu button for toggling sidebar */}
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10 focus:outline-none sm:hidden"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>

//               <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 {/* <span className="text-sm font-semibold tracking-wide text-white/80">
//                   {LoginUserName}
//                 </span> */}
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={loginUserCode}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 { label: "Account", onClick: (navigate) => navigate("/account") },
//                 { label: "Settings", onClick: (navigate) => navigate("/settings") },
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Layout */}
//       <div className="flex flex-1 overflow-hidden">
//         {/* Sidebar */}
//         <Sidebar sidebarOpen={sidebarOpen} />

//         {/* Content */}
//         <main
//           className={`flex-1 p-4 transition-all duration-300 ${
//             sidebarOpen ? "ml-0 " : "ml-0"
//           }`}
//         >
//           <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 text-slate-900">

//             {/* ---------------- Customer Header Here ---------------- */}
//             <ContentHeader
//               name={authUser?.business_name || "-"}
//               city={authUser?.city || "N/A"}
//               joinedOn={authUser?.created_at?.slice(0, 10) || "2024"}
//               email={authUser?.email || "-"}
//               mobile={authUser?.mobile || "-"}
//               loginUserCreated={authUser?.created_at || "-"}
//             />

//             {/* Page content */}

//             <div className="p-3"> 
//               <Outlet />
//             </div>
            

//           </div>
//         </main>

//       </div>
//     </div>
//   );
// }



// import { useEffect, useState } from "react";
// import { useNavigate, NavLink, Outlet } from "react-router-dom";
// import {
//   Menu,
//   ChevronDown,
//   LayoutDashboard,
//   Users,
//   KeyRound,
//   Package,
//   ClipboardList,
//   FileText,
//   Ticket,
//   Settings,
// } from "lucide-react";
// import LogoImg from "../asset/images/GoelectroniX.png";
// import ProfileDropdown from "../Component/ProfileDropdown";

// export default function HubPanel() {
//   // Sidebar open by default for small screens (<640px)
//   const [sidebarOpen, setSidebarOpen] = useState(true);
//   const [showDropdown, setShowDropdown] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const authUserData = localStorage.getItem("authUserData");
//     if (!authUserData) navigate("/login");

//     // Toggle sidebar automatically when resizing
//     const handleResize = () => {
//       setSidebarOpen(window.innerWidth < 640);
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, [navigate]);

//   const handleLogout = () => {
//     localStorage.clear();
//     navigate("/login");
//   };

//   const nav = [
//     { path: "/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
//     { path: "/allemployees", icon: <Users size={18} />, label: "Employees" },
//     { path: "/allagents", icon: <Users size={18} />, label: "Agents" },
//     { path: "/allretailers", icon: <Users size={18} />, label: "Retailers" },
//     { path: "/onboard", icon: <Users size={18} />, label: "Retailer Onboard" },
//     { path: "/employeedashboard", icon: <Users size={18} />, label: "Employee Dashboard" },
//     { path: "/operationdashboard", icon: <Users size={18} />, label: "Operation Dashboard" },
//     { path: "/agentdashboard", icon: <Users size={18} />, label: "Agent Dashboard" },
//     { path: "/retailerdashboard", icon: <Users size={18} />, label: "Retailer Dashboard" },
//     { path: "/userdashboard", icon: <Users size={18} />, label: "User Dashboard" },
//     { path: "/custdashboard", icon: <Users size={18} />, label: "Cust Dashboard" },
    
    
//     // { path: "/keys", icon: <KeyRound size={18} />, label: "Keys" },
//     // { path: "/inventory", icon: <Package size={18} />, label: "Inventory" },
//     // { path: "/orders", icon: <ClipboardList size={18} />, label: "Orders" },
//     // { path: "/invoices", icon: <FileText size={18} />, label: "Invoices" },
//     // { path: "/support", icon: <Ticket size={18} />, label: "Support" },
//     // { path: "/account", icon: <Settings size={18} />, label: "Account" },
//   ];

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString);
//   const loginUserID = authUser?.id;
//   const LoginUserName = authUser?.business_name;
//   const loginUserCode = authUser?.org_code;

//   return (
//     <div className="min-h-screen bg-white text-slate-900">
//       {/* Top Bar */}
//       <header className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: "#104191ff" }}>
//         <div className="mx-auto px-3 sm:px-6 lg:px-8">
//           <div className="flex h-14 items-center justify-between">
//             <div className="flex items-center gap-2">
//               {/* Hide Menu button on small screens since sidebar is already visible */}
//               <button
//                 onClick={() => setSidebarOpen((s) => !s)}
//                 className="rounded-xl p-2 hover:bg-white/10 focus:outline-none hidden sm:inline-flex"
//               >
//                 <Menu className="text-white" size={20} />
//               </button>

//               <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
//                 <img src={LogoImg} alt="Logo" className="h-8 w-auto" />
//                 <span className="text-sm font-semibold tracking-wide text-white/80">
//                   {LoginUserName}
//                 </span>
//               </div>
//             </div>

//             <ProfileDropdown
//               userCode={loginUserCode}
//               avatar="/favicon.jpg"
//               onLogout={handleLogout}
//               menuItems={[
//                 {
//                   label: "Account",
//                   onClick: (navigate) => navigate("/account"),
//                 },
//                 {
//                   label: "Settings",
//                   onClick: (navigate) => navigate("/settings"),
//                 }
//               ]}
//             />
//           </div>
//         </div>
//       </header>

//       {/* Layout */}
//       <div className="mx-auto px-3 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-12 gap-3 sm:gap-4 py-3 sm:py-4">
//           {/* Sidebar for large screens */}
//           {sidebarOpen && (
//             <aside className="hidden sm:block col-span-12 sm:col-span-4 lg:col-span-2">
//               <div className="sticky top-16 space-y-2 rounded-2xl p-2 bg-slate-50">
//                 {nav.map((n) => (
//                   <NavLink
//                     key={n.path}
//                     to={n.path}
//                     className={({ isActive }) =>
//                       `flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm ${
//                         isActive ? "bg-white font-semibold" : "hover:bg-white"
//                       }`
//                     }
//                   >
//                     <span className="text-slate-700">{n.icon}</span>
//                     <span className="text-slate-700">{n.label}</span>
//                   </NavLink>
//                 ))}
//               </div>
//             </aside>
//           )}

//           {/* Content */}
//           <main
//             className={
//               sidebarOpen ? "col-span-12 sm:col-span-8 lg:col-span-10" : "col-span-12"
//             }
//           >
//             <Outlet />
//           </main>
//         </div>
//       </div>

//       {/* Bottom Navigation for Small Screens */}
//       {sidebarOpen && (
//         <nav className="fixed bottom-0 left-0 right-0 z-50 overflow-x-auto bg-white border-t shadow sm:hidden">
//           <div className="flex w-max">
//             {nav.map((n) => (
//               <NavLink
//                 key={n.path}
//                 to={n.path}
//                 className={({ isActive }) =>
//                   `flex-shrink-0 w-1/4 flex flex-col items-center justify-center py-2 text-xs ${
//                     isActive ? "text-blue-600" : "text-slate-600"
//                   }`
//                 }
//               >
//                 {n.icon}
//                 <span>{n.label}</span>
//               </NavLink>
//             ))}
//           </div>
//         </nav>
//       )}

//       <footer className="mt-4 sm:mt-6 border-t py-4 text-center text-xs text-slate-500">
//         © {new Date().getFullYear()} RR Tech — Warrenty Hub Panel
//       </footer>
//     </div>
//   );
// }
